import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleCrudPermissions } from "../../hooks/useModuleCrudPermissions.js";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Stack,
  InputAdornment,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Snackbar,
  Alert,
  Select,
  TableHead,
  Badge,
  Checkbox,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Dashboard as DashboardIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Settings as SettingsIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  CalendarToday as CalendarTodayIcon,
  Visibility as VisibilityIcon,
  Videocam as VideocamIcon,
  FilterList as FilterListIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
// import { Layout } from './components/Layout';
import { AddAlertRule } from "../../components/alertServicesSections/AddAlertRule";
import { Dashboard } from "./Dashboard";
import { HistoryEnhanced } from "./HistoryEnhanced";
import { AlertTypesReference } from "../../components/alertServicesSections/AlertTypesReference";
import { AlertRuleGridView } from "../../components/alertServicesSections/AlertRuleGridView";
import { EmptyState } from "../../components/alertServicesSections/EmptyState";
import { AlertRulesSettings } from "../../components/alertServicesSections/AlertRulesSettings";
import {
  AlertDataProvider,
  useAlertData,
} from "../../contexts/AlertDataContext";
import { useFooter } from "../../contexts/FooterContext";
import {
  alertRulesAPI,
  triggersAPI,
  userSettingsAPI,
  camerasAPI,
  useCasesAPI,
} from "../../services/api";
import { sourceRefsWithZonesFromApiRule } from "../../utils/alertRuleSourceRefsUtils";
const ALERT_RULES_FAVORITE_KEYS = "alert-rules-favorite-keys";
const ALERT_RULES_SHOW_FAVORITES = "alert-rules-show-favorites";

const SEVERITY_FILTER_ORDER = ["Critical", "High", "Medium", "Low"];

/** Same as AddAlertRule.jsx — Sub Source entries use `cameraId@@roiId` for AI rules. */
const CAMERA_ZONE_ENTRY_SEP = "@@";

/** Rule row has `source_refs` / `zone_refs` from API (opens name popup instead of full editor). */
function ruleHasSubSourceRefsPopupData(rule) {
  return (
    (Array.isArray(rule?.sourceRefsWithZones) &&
      rule.sourceRefsWithZones.length > 0) ||
    (Array.isArray(rule?.sourceRefsList) && rule.sourceRefsList.length > 0) ||
    (Array.isArray(rule?.zoneRefsList) && rule.zoneRefsList.length > 0)
  );
}

function subSourceAccordionItemKey(row, index) {
  return String(row?.id || row?.name || `idx-${index}`);
}

/** Legacy rows without `sourceRefsWithZones` (e.g. mock data). */
function buildSubSourceAccordionFallback(rule) {
  const src = Array.isArray(rule?.sourceRefsList) ? rule.sourceRefsList : [];
  const zones = Array.isArray(rule?.zoneRefsList) ? rule.zoneRefsList : [];
  if (src.length === 0 && zones.length === 0) return [];
  if (src.length === 0) {
    return [{ id: "", name: "Zones", zoneRefsList: zones }];
  }
  if (zones.length === 0) {
    return src.map((s) => ({
      id: s.id ?? "",
      name: s.name ?? "",
      zoneRefsList: [],
    }));
  }
  return src.map((s, i) => ({
    id: s.id ?? "",
    name: s.name ?? "",
    zoneRefsList: i === 0 ? zones : [],
  }));
}

function ruleCanInteractSubSourceColumn(rule) {
  return (
    ruleHasSubSourceRefsPopupData(rule) ||
    Boolean(String(rule?.apiId ?? rule?.id ?? "").trim())
  );
}

/** True if value looks like a use case / UUID id (send as `alert_type` unchanged). */
function isProbableUsecaseId(value) {
  const s = String(value ?? "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s,
  );
}

function extractUseCasesListFromResponse(body) {
  if (!body || typeof body !== "object") return [];
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.results)) return body.results;
  if (Array.isArray(body)) return body;
  return [];
}

function extractCamerasArray(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const arr = body.data ?? body.results ?? body.Data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

/**
 * `selectedIds` are use-case UUIDs from GET /use-cases/. Match `rule.useCaseId`, or
 * legacy rows with only a display `rule.alertType` name.
 */
function alertRuleMatchesUseCaseSelection(rule, selectedIds, idToLabel) {
  if (!selectedIds.length) return false;
  const uc = String(rule?.useCaseId ?? "").trim();
  if (uc) return selectedIds.includes(uc);
  const display = String(rule?.alertType ?? "").trim();
  if (!display) return false;
  for (const id of selectedIds) {
    if ((idToLabel.get(id) ?? "") === display) return true;
  }
  return false;
}

/** `selectedCameraIds` from GET /cameras `id`; match `rule.sourceIds` or source display name. */
function alertRuleMatchesCameraSelection(rule, selectedCameraIds, idToName) {
  if (!selectedCameraIds.length) return false;
  const idSet = new Set(selectedCameraIds.map((x) => String(x)));
  const ruleIds = Array.isArray(rule?.sourceIds)
    ? rule.sourceIds
        .map((x) => String(x ?? "").trim())
        .filter(Boolean)
    : [];
  if (ruleIds.some((rid) => idSet.has(rid))) return true;
  const label = String(rule?.subSourceLabel ?? rule?.sourceName ?? "").trim();
  if (!label) return false;
  for (const cid of selectedCameraIds) {
    const camName = String(idToName.get(cid) ?? "").trim();
    if (camName && camName === label) return true;
  }
  return false;
}

function resolveUsecaseIdFromRuleData(ruleData) {
  if (!ruleData || typeof ruleData !== "object") return "";
  if (
    ruleData.usecase_id != null &&
    String(ruleData.usecase_id).trim() !== ""
  ) {
    return String(ruleData.usecase_id).trim();
  }
  const at = ruleData.alert_type;
  if (at != null && isProbableUsecaseId(String(at))) {
    return String(at).trim();
  }
  return "";
}

/** `alert_type` in rules API: use case id when UUID; else legacy slug from name. */
function mapAlertTypeForRulesApi(alertTypeField) {
  const raw = alertTypeField ?? "";
  if (isProbableUsecaseId(raw)) {
    return String(raw).trim();
  }
  return String(raw || "security")
    .toLowerCase()
    .replace(/\s+/g, "_");
}

/** Normalize GET /cameras/:id/zones response to an array of zone objects. */
function extractZonesArrayFromListZonesBody(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const arr = body.data ?? body.results ?? body.Data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

async function fetchZoneIdSetForCamera(cameraId) {
  try {
    const body = await camerasAPI.listZones(cameraId);
    const raw = extractZonesArrayFromListZonesBody(body);
    return new Set(
      raw.map((z) => String(z?.id ?? "").trim()).filter(Boolean),
    );
  } catch (e) {
    console.warn("listZones failed for camera", cameraId, e);
    return new Set();
  }
}

/**
 * Build AddAlertRule `selectedSources` from GET /rules/:id (or list row) data.
 * Never use display names like `source_name` here — the API expects real source_ids on save.
 *
 * When several cameras share more ROIs than cameras (e.g. 2 sources + 3 roi_ids), we
 * resolve each roi_id to its camera via GET /cameras/:id/zones so entries include
 * `cameraId@@roiId` and the edit form count / zone checkboxes match the API.
 */
async function buildSelectedSourcesForEditForm(ruleData, sourceCategory) {
  const st = String(ruleData?.source_type || "").toLowerCase();
  let sourceIds = [];
  if (Array.isArray(ruleData?.source_ids) && ruleData.source_ids.length > 0) {
    sourceIds = ruleData.source_ids
      .map((x) => String(x).trim())
      .filter(Boolean);
  } else if (
    ruleData?.source_id != null &&
    String(ruleData.source_id).trim() !== ""
  ) {
    sourceIds = [String(ruleData.source_id).trim()];
  }

  const roiIds = Array.isArray(ruleData?.roi_ids)
    ? ruleData.roi_ids.map((x) => String(x).trim()).filter(Boolean)
    : [];

  if (sourceCategory === "IoT" || sourceCategory === "System") {
    return sourceIds;
  }
  if (st === "iot_device" || st === "iot" || st === "system") {
    return sourceIds;
  }

  const isCamera =
    sourceCategory === "AI" ||
    st === "camera" ||
    (sourceCategory !== "IoT" &&
      sourceCategory !== "System" &&
      (st === "" || !st));

  if (!isCamera) {
    return sourceIds;
  }

  if (sourceIds.length === 0) {
    return [];
  }

  if (sourceIds.length === 1) {
    const cam = sourceIds[0];
    if (roiIds.length === 0) {
      return [cam];
    }
    return [
      cam,
      ...roiIds.map((r) => `${cam}${CAMERA_ZONE_ENTRY_SEP}${r}`),
    ];
  }

  if (roiIds.length === 0) {
    return [...sourceIds];
  }

  if (sourceIds.length === roiIds.length) {
    const out = [];
    for (let i = 0; i < sourceIds.length; i++) {
      out.push(sourceIds[i]);
      out.push(`${sourceIds[i]}${CAMERA_ZONE_ENTRY_SEP}${roiIds[i]}`);
    }
    return out;
  }

  const zoneSets = await Promise.all(
    sourceIds.map(async (sid) => ({
      sid,
      zoneIds: await fetchZoneIdSetForCamera(sid),
    })),
  );

  const out = [...sourceIds];
  for (const rid of roiIds) {
    if (!rid) continue;
    let owner = "";
    for (const { sid, zoneIds } of zoneSets) {
      if (zoneIds.has(rid)) {
        owner = sid;
        break;
      }
    }
    if (!owner) {
      owner = sourceIds[0];
    }
    out.push(`${owner}${CAMERA_ZONE_ENTRY_SEP}${rid}`);
  }
  return out;
}

/** Stable key for persisting favorites (API id preferred, else local id). */
function getAlertRuleFavoriteKey(rule) {
  if (!rule || typeof rule !== "object") return "";
  if (rule.apiId != null && String(rule.apiId).trim() !== "") {
    return String(rule.apiId);
  }
  if (rule.id != null && String(rule.id).trim() !== "") {
    return String(rule.id);
  }
  return "";
}

function AppContent({ alertRulesOnly = false }) {
  const { create: canCreateRule, update: canUpdateRule, delete: canDeleteRule } =
    useModuleCrudPermissions("rules");
  const navigate = useNavigate();
  const {
    alertRules,
    addAlertRule,
    updateAlertRule,
    deleteAlertRule,
    toggleAlertRuleActive,
    loadAlertRulesFromAPI,
  } = useAlertData();
  const { setIsFooterVisible } = useFooter();

  const [currentTab, setCurrentTab] = useState(0);
  const isRulesView = alertRulesOnly;
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [contextMenuPosition, setContextMenuPosition] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [orderBy, setOrderBy] = useState("subSourceLabel");
  const [order, setOrder] = useState("asc");
  const [ruleAlertTypeMenuAnchor, setRuleAlertTypeMenuAnchor] =
    useState(null);
  const [ruleSourceNameMenuAnchor, setRuleSourceNameMenuAnchor] =
    useState(null);
  const [selectedRuleAlertTypes, setSelectedRuleAlertTypes] = useState([]);
  const [isRuleAlertTypeAllChecked, setIsRuleAlertTypeAllChecked] =
    useState(true);
  const [selectedRuleSourceNames, setSelectedRuleSourceNames] = useState([]);
  const [isRuleSourceNameAllChecked, setIsRuleSourceNameAllChecked] =
    useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  /** Sub-source popup: accordion of sub-sources + zones (list API). */
  const [subSourceRefsDialog, setSubSourceRefsDialog] = useState(null);
  /** Single-expanded accordion panel key inside Sub source & Zones dialog. */
  const [subSourceAccordionExpanded, setSubSourceAccordionExpanded] =
    useState(null);
  const [showAddAlertForm, setShowAddAlertForm] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [editingRuleApiId, setEditingRuleApiId] = useState(null); // Store the API rule ID for update
  /** When true, AddAlertRule opens read-only (View from row menu). */
  const [alertFormViewOnly, setAlertFormViewOnly] = useState(false);
  /** After loading this rule id in view mode, auto-open Sub Source dialog (table link). */
  const [pendingSubSourceBrowseRuleApiId, setPendingSubSourceBrowseRuleApiId] =
    useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [viewMode, setViewMode] = useState("list");
  const [hoveredRow, setHoveredRow] = useState(null);
  const [favoriteRuleKeys, setFavoriteRuleKeys] = useState([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showReferenceGuide, setShowReferenceGuide] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [cooldownMin, setCooldownMin] = useState(0);
  const [cooldownMax, setCooldownMax] = useState(3600);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const fetchingRulesRef = useRef(false); // Ref to prevent multiple simultaneous API calls
  const [hasApiResponse, setHasApiResponse] = useState(false); // Track if API has responded
  /** GET /use-cases/ — drives Alert Type filter options on Alert Rules tab. */
  const [ruleTabUseCases, setRuleTabUseCases] = useState([]);
  const [ruleTabUseCasesLoading, setRuleTabUseCasesLoading] = useState(false);
  /** GET /cameras — drives Source filter options (values are camera ids). */
  const [ruleTabCameras, setRuleTabCameras] = useState([]);
  const [ruleTabCamerasLoading, setRuleTabCamerasLoading] = useState(false);

  // Date filter states
  // Default "all" so the rules table lists every rule from the API; date presets filter createdDate.
  const [dateRange, setDateRange] = useState("all");
  const [dateRangeMenuAnchor, setDateRangeMenuAnchor] = useState(null);
  const [severityStatusFilterAnchor, setSeverityStatusFilterAnchor] =
    useState(null);
  const [selectedRuleSeverities, setSelectedRuleSeverities] = useState([]);
  const [isRuleSeverityAllChecked, setIsRuleSeverityAllChecked] =
    useState(true);
  const [selectedRuleActiveStates, setSelectedRuleActiveStates] = useState(
    [],
  );
  const [isRuleActiveStateAllChecked, setIsRuleActiveStateAllChecked] =
    useState(true);
  const [customDateDialog, setCustomDateDialog] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [viewingMonth, setViewingMonth] = useState(new Date());

  // Hide footer when Add Rule form is shown
  useEffect(() => {
    setIsFooterVisible(!showAddAlertForm);
  }, [showAddAlertForm, setIsFooterVisible]);

  /** Sub source & Zones: first accordion open when dialog opens. */
  useEffect(() => {
    if (!subSourceRefsDialog) {
      setSubSourceAccordionExpanded(null);
      return;
    }
    const items = subSourceRefsDialog.accordionItems || [];
    if (!items.length) {
      setSubSourceAccordionExpanded(null);
      return;
    }
    setSubSourceAccordionExpanded(subSourceAccordionItemKey(items[0], 0));
  }, [subSourceRefsDialog]);

  useEffect(() => {
    try {
      localStorage.setItem(
        ALERT_RULES_FAVORITE_KEYS,
        JSON.stringify(favoriteRuleKeys),
      );
    } catch (error) {
      console.error("Error saving alert rule favorites:", error);
    }
  }, [favoriteRuleKeys]);

  useEffect(() => {
    try {
      localStorage.setItem(
        ALERT_RULES_SHOW_FAVORITES,
        String(showFavoritesOnly),
      );
    } catch (error) {
      console.error("Error saving favorites filter:", error);
    }
    setPage(1);
  }, [showFavoritesOnly]);

  // Fetch rules from API when Alert Rules view is active (admin page or legacy tab index 2)
  useEffect(() => {
    if (isRulesView && !fetchingRulesRef.current) {
      fetchingRulesRef.current = true; // Set flag to prevent multiple calls

      const fetchRules = async () => {
        try {
          setHasApiResponse(false); // Reset flag when starting new fetch
          console.log("Fetching rules from API...");
          const response = await alertRulesAPI.list();
          console.log("Rules fetched from API:", response);

          // Handle different response formats
          // API returns: { success: true, message: "...", data: { rules: [...] } }
          const rules =
            response.data?.rules ||
            response.rules ||
            (Array.isArray(response.data) ? response.data : null) ||
            (Array.isArray(response) ? response : null);

          // If no response or empty response, don't display anything
          if (
            !response ||
            (!response.data && !response.rules && !Array.isArray(response))
          ) {
            console.log("No response from API, not displaying anything");
            setHasApiResponse(false);
            loadAlertRulesFromAPI([]);
          } else if (Array.isArray(rules) && rules.length > 0) {
            console.log("Loading rules into state:", rules);
            setHasApiResponse(true);
            loadAlertRulesFromAPI(rules);
          } else if (
            rules &&
            typeof rules === "object" &&
            !Array.isArray(rules)
          ) {
            // If response is a single object, wrap it in an array
            console.log("Loading single rule into state:", rules);
            setHasApiResponse(true);
            loadAlertRulesFromAPI([rules]);
          } else {
            console.log("No rules found in response, not displaying anything");
            setHasApiResponse(true); // API responded but with empty data
            loadAlertRulesFromAPI([]);
          }
        } catch (error) {
          console.error("Error fetching rules from API:", error);
          // Keep existing rules if API call fails
        } finally {
          fetchingRulesRef.current = false; // Reset flag after API call completes
        }
      };

      fetchRules();
    }
  }, [isRulesView]); // Only depend on rules view, not loadAlertRulesFromAPI

  useEffect(() => {
    if (!isRulesView) return;
    let cancelled = false;
    setRuleTabUseCasesLoading(true);
    setRuleTabCamerasLoading(true);
    (async () => {
      const [ucSettled, camSettled] = await Promise.allSettled([
        useCasesAPI.list(),
        camerasAPI.list(),
      ]);
      if (cancelled) return;

      if (ucSettled.status === "fulfilled") {
        const raw = extractUseCasesListFromResponse(ucSettled.value);
        const normalized = raw
          .map((uc) => ({
            id: String(uc?.id ?? "").trim(),
            name: String(uc?.name ?? "").trim(),
          }))
          .filter((x) => x.id);
        setRuleTabUseCases(normalized);
      } else {
        console.error(
          "Alert Rules: failed to load use cases list:",
          ucSettled.reason,
        );
        setRuleTabUseCases([]);
      }
      setRuleTabUseCasesLoading(false);

      if (camSettled.status === "fulfilled") {
        const raw = extractCamerasArray(camSettled.value);
        const normalized = raw
          .map((c) => ({
            id: String(c?.id ?? "").trim(),
            name: String(c?.name ?? "").trim(),
          }))
          .filter((x) => x.id);
        setRuleTabCameras(normalized);
      } else {
        console.error(
          "Alert Rules: failed to load cameras list:",
          camSettled.reason,
        );
        setRuleTabCameras([]);
      }
      setRuleTabCamerasLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [isRulesView]);

  const handleTabChange = (_event, newValue) => {
    if (alertRulesOnly) return;
    setCurrentTab(newValue);
    fetchingRulesRef.current = false;
    setHasApiResponse(false);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleToggleActive = async (rule) => {
    const ruleApiId = rule.apiId ?? rule.id;
    const newEnabled = !rule.isActive;
    try {
      await alertRulesAPI.update(ruleApiId, { is_enabled: newEnabled });
      toggleAlertRuleActive(rule.id);
    } catch (error) {
      console.error("Error toggling rule:", error);
      setSnackbarMessage(
        error?.message || "Failed to update rule. Please try again.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleMenuOpen = (event, alert) => {
    setContextMenuPosition(null);
    setAnchorEl(event.currentTarget);
    setSelectedAlert(alert);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setContextMenuPosition(null);
    setSelectedAlert(null);
  };

  const handleContextMenu = (event, alert) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(null);
    setContextMenuPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedAlert(alert);
  };

  const handleSubSourceColumnClick = async (event, rule) => {
    event.preventDefault();
    event.stopPropagation();
    if (ruleHasSubSourceRefsPopupData(rule)) {
      const ruleId = rule.apiId ?? rule.id;
      let accordionItems =
        Array.isArray(rule.sourceRefsWithZones) &&
        rule.sourceRefsWithZones.length > 0
          ? rule.sourceRefsWithZones
          : buildSubSourceAccordionFallback(rule);
      if (ruleId) {
        try {
          const response = await alertRulesAPI.get(ruleId);
          const full = response?.data ?? response;
          const live = sourceRefsWithZonesFromApiRule(full);
          if (live.length > 0) {
            accordionItems = live;
          }
        } catch (err) {
          console.error("Failed to load rule for Sub source & Zones:", err);
        }
      }
      if (accordionItems.length > 0) {
        setSubSourceRefsDialog({
          ruleName: rule.name || rule.alertName || "Alert rule",
          accordionItems,
        });
        return;
      }
    }
    const apiId = String(rule?.apiId ?? rule?.id ?? "").trim();
    if (!apiId) return;
    setPendingSubSourceBrowseRuleApiId(apiId);
    await loadAlertRuleIntoEditor(rule, true);
  };

  const loadAlertRuleIntoEditor = async (row, viewOnlyMode) => {
    if (!row) return;
    setAlertFormViewOnly(viewOnlyMode);
    try {
        // Use apiId if available, otherwise try to use the id directly
        const ruleApiId = row.apiId || row.id;

        console.log("Fetching rule data for edit:", ruleApiId);
        const response = await alertRulesAPI.get(ruleApiId);
        const ruleData = response?.data ?? response;
        console.log("Rule data fetched:", ruleData);

        // Fetch triggers associated with this rule using /triggers/rule/:rule_id
        let triggers = [];
        try {
          console.log("Fetching triggers for rule:", ruleApiId);
          const triggersResponse = await triggersAPI.getByRuleId(ruleApiId);
          console.log("Triggers fetched for rule:", triggersResponse);

          // Handle different response formats
          let allTriggers = [];
          if (
            triggersResponse?.data?.triggers &&
            Array.isArray(triggersResponse.data.triggers)
          ) {
            // Response structure: { data: { triggers: [...] } }
            allTriggers = triggersResponse.data.triggers;
            console.log("Extracted triggers from response.data.triggers");
          } else if (
            triggersResponse?.triggers &&
            Array.isArray(triggersResponse.triggers)
          ) {
            // Response structure: { triggers: [...] }
            allTriggers = triggersResponse.triggers;
            console.log("Extracted triggers from response.triggers");
          } else if (
            triggersResponse?.data &&
            Array.isArray(triggersResponse.data)
          ) {
            // Response structure: { data: [...] }
            allTriggers = triggersResponse.data;
            console.log("Extracted triggers from response.data array");
          } else if (Array.isArray(triggersResponse)) {
            // Response structure: [...]
            allTriggers = triggersResponse;
            console.log("Extracted triggers from response array");
          } else if (
            triggersResponse?.data &&
            typeof triggersResponse.data === "object"
          ) {
            // Response structure: { data: { ...single trigger } }
            allTriggers = [triggersResponse.data];
            console.log("Extracted single trigger from response.data object");
          } else if (triggersResponse && typeof triggersResponse === "object") {
            // Response structure: { ...single trigger }
            allTriggers = [triggersResponse];
            console.log("Extracted single trigger from response object");
          }

          triggers = allTriggers;
          console.log("Final triggers array for rule:", triggers);
          console.log("Number of triggers:", triggers.length);
        } catch (error) {
          console.error("Error fetching triggers:", error);
          // Continue without triggers if API call fails
        }

        // Transform triggers to action list format
        const transformedActions = triggers.map((trigger, index) => {
          console.log(
            `Processing trigger ${index}:`,
            JSON.stringify(trigger, null, 2),
          );

          // Normalize action type - handle case variations and different field names
          const rawAction = trigger.action || trigger.action_type || "";
          const normalizedAction = rawAction
            ? String(rawAction).toUpperCase().trim()
            : "";

          console.log(`Trigger ${index} - rawAction:`, rawAction);
          console.log(`Trigger ${index} - normalizedAction:`, normalizedAction);

          let actionType = "";
          let payload = "";
          let details = {};

          // Get config - try multiple locations
          const config = trigger.config || trigger;

          if (normalizedAction === "EMAIL") {
            actionType = "Email";
            payload =
              config.recipients?.join(", ") ||
              config.mail_ids?.join(", ") ||
              config.email ||
              "";
            details = {
              type: "Email",
              email: payload,
              subject: config.subject || trigger.name || "",
              messageBody:
                config.message_body || config.body || config.message || "",
              cc: Array.isArray(config.cc)
                ? config.cc.join(", ")
                : config.cc || "",
              bcc: Array.isArray(config.bcc)
                ? config.bcc.join(", ")
                : config.bcc || "",
            };
          } else if (normalizedAction === "TELEGRAM") {
            actionType = "Telegram";
            const chatIds = config.chat_ids || config.chat_id || [];
            payload = Array.isArray(chatIds)
              ? chatIds.join(", ")
              : chatIds || "";
            details = {
              type: "Telegram",
              chatId: payload,
              messageBody:
                config.message_body || config.body || config.message || "",
            };
          } else if (normalizedAction === "SMS") {
            actionType = "SMS";
            const phoneNumbers =
              config.phone_numbers || config.phone_number || [];
            payload = Array.isArray(phoneNumbers)
              ? phoneNumbers.join(", ")
              : phoneNumbers || "";
            details = {
              type: "SMS",
              chatId: payload,
              messageBody:
                config.message_body || config.body || config.message || "",
            };
          } else if (normalizedAction === "HTTP") {
            actionType = "HTTP";
            payload = config.url || "";
            details = {
              type: "HTTP",
              chatId: payload,
              messageBody:
                config.message_body || config.body || config.message || "",
              httpMethod: config.method || "POST",
            };
          } else {
            // If action type is not recognized, log warning but don't default to Email
            console.warn(
              `Unknown action type for trigger ${index}:`,
              normalizedAction,
            );
            console.warn("Trigger data:", trigger);
            // Only default to Email if we truly have no action type
            actionType = normalizedAction || "Email";
            details = {
              type: actionType,
            };
          }

          console.log(`Trigger ${index} - Final actionType:`, actionType);

          const triggerEnabled =
            trigger.is_enabled === false || trigger.is_enabled === 0
              ? false
              : trigger.is_enabled === true || trigger.is_enabled === 1
                ? true
                : trigger.is_active === false
                  ? false
                  : trigger.is_active === true
                    ? true
                    : true;

          return {
            id: Date.now().toString() + Math.random(), // Generate unique ID
            type: actionType,
            payload: payload,
            triggerId: trigger.id, // Store trigger ID for update
            isActive: triggerEnabled,
            details: details,
          };
        });

        const ruleDisabled =
          ruleData.is_enabled === false || ruleData.is_enabled === 0;
        const transformedActionsForRule =
          ruleDisabled && transformedActions.length > 0
            ? transformedActions.map((a) => ({ ...a, isActive: false }))
            : transformedActions;

        // Transform API response to form format
        const apiAlertType = ruleData.alert_type || row.alertType;
        const alertTypeNameRaw =
          ruleData.alert_type_name != null &&
          String(ruleData.alert_type_name).trim() !== ""
            ? String(ruleData.alert_type_name).trim()
            : ruleData.alertTypeName != null &&
                String(ruleData.alertTypeName).trim() !== ""
              ? String(ruleData.alertTypeName).trim()
              : null;
        const alertTypeFromSlug =
          apiAlertType &&
          String(apiAlertType)
            .split("_")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(" ");
        const alertTypeForForm =
          alertTypeNameRaw || alertTypeFromSlug || "";

        // Map severity from API (e.g. "high") to form format ("High")
        const apiSeverity =
          ruleData.rule_severity ||
          ruleData.severity ||
          row.severity ||
          "medium";
        const severityForForm =
          apiSeverity &&
          String(apiSeverity).charAt(0).toUpperCase() +
            String(apiSeverity).slice(1).toLowerCase();

        // Pass schedule from API as-is when in day-key format (monday, tuesday, etc.)
        // Empty days stay empty; only actual time slots from API are shown
        const isDayKeySchedule = (s) =>
          s &&
          typeof s === "object" &&
          !s.type &&
          !s.start_time &&
          !s.days &&
          (s.monday !== undefined ||
            s.tuesday !== undefined ||
            s.wednesday !== undefined);
        const scheduleForForm = ruleData.schedule
          ? isDayKeySchedule(ruleData.schedule)
            ? ruleData.schedule
            : {
                type: "forever",
                startTime: ruleData.schedule.start_time || "00:00",
                endTime: ruleData.schedule.end_time || "23:59",
                recurringDays: ruleData.schedule.days || [],
              }
          : null;

        const st = String(ruleData.source_type || "").toLowerCase();
        const sourceCategoryFromApi =
          ruleData.source_category ||
          row.sourceCategory ||
          (st.includes("iot") ? "IoT" : st.includes("system") ? "System" : "AI");

        const transformedRule = {
          id: String(ruleData.id || row.id),
          alertName: ruleData.alert_name || ruleData.name || row.name,
          alertType: alertTypeForForm || apiAlertType || row.alertType,
          usecaseId: resolveUsecaseIdFromRuleData(ruleData),
          sourceCategory: sourceCategoryFromApi,
          selectedSources: await buildSelectedSourcesForEditForm(
            ruleData,
            sourceCategoryFromApi,
          ),
          severity: severityForForm || apiSeverity,
          schedule: scheduleForForm,
          countdownInterval: String(
            ruleData.cooldown_interval ??
              ruleData.countdown_interval ??
              row.countdownInterval ??
              20,
          ),
          actions:
            transformedActionsForRule.length > 0
              ? transformedActionsForRule
              : row.actionTypes?.map((type) => ({ type })) || [],
        };

        setEditingRuleApiId(ruleData.id || ruleApiId); // Store API ID for update
        setEditingAlert(transformedRule);
        setShowAddAlertForm(true);
      } catch (error) {
        console.error("Error fetching rule for edit:", error);
        setEditingRuleApiId(row.apiId || row.id);
        const fallbackSources =
          Array.isArray(row.sourceIds) && row.sourceIds.length > 0
            ? row.sourceIds.map(String)
            : Array.isArray(row.selectedSources) && row.selectedSources.length > 0
              ? row.selectedSources.filter(
                  (id) =>
                    id != null &&
                    String(id).trim() !== "" &&
                    String(id).trim().toLowerCase() !== "source",
                )
              : [];
        setEditingAlert({ ...row, selectedSources: fallbackSources });
        setShowAddAlertForm(true);
      }
  };

  const handleEdit = async () => {
    if (selectedAlert) {
      await loadAlertRuleIntoEditor(selectedAlert, false);
    }
    handleMenuClose();
  };

  const handleView = async () => {
    if (selectedAlert) {
      await loadAlertRuleIntoEditor(selectedAlert, true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedAlert) {
      try {
        // Use apiId if available, otherwise use the id directly
        const ruleApiId = selectedAlert.apiId || selectedAlert.id;

        console.log("Deleting rule:", ruleApiId);
        await alertRulesAPI.delete(ruleApiId);
        console.log("Rule deleted successfully");

        // Also remove from local state for immediate UI update
        deleteAlertRule(selectedAlert.id);

        setSnackbarMessage(
          `Alert rule "${selectedAlert.name}" deleted successfully.`,
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        // Refresh rules list from API
        fetchingRulesRef.current = false;
        const rulesResponse = await alertRulesAPI.list();
        const rules =
          rulesResponse.data?.rules ||
          rulesResponse.rules ||
          (Array.isArray(rulesResponse.data) ? rulesResponse.data : null) ||
          (Array.isArray(rulesResponse) ? rulesResponse : []);
        if (Array.isArray(rules) && rules.length > 0) {
          loadAlertRulesFromAPI(rules);
        } else {
          loadAlertRulesFromAPI([]);
        }
      } catch (error) {
        console.error("Error deleting rule:", error);
        let errorMessage = "Failed to delete alert rule. Please try again.";
        if (error.status === 404) {
          errorMessage = "Rule not found. It may have already been deleted.";
        } else if (error.message) {
          errorMessage = error.message;
        }
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
    setDeleteDialogOpen(false);
    handleMenuClose();
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  /** Rules API allows hours 00–23 only; map end-of-day away from "24:00". */
  const normalizeScheduleTimesForRulesApi = (schedule) => {
    if (!schedule || typeof schedule !== "object") return schedule;
    const out = {};
    for (const [day, slots] of Object.entries(schedule)) {
      if (!Array.isArray(slots)) {
        out[day] = slots;
        continue;
      }
      out[day] = slots.map((slot) => {
        if (!slot || typeof slot !== "object") return slot;
        let { start, end } = slot;
        if (end === "24:00" || end === "24:00:00") end = "23:59";
        if (start === "24:00" || start === "24:00:00") start = "23:59";
        return { ...slot, start, end };
      });
    }
    return out;
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const favoriteKeySet = useMemo(
    () => new Set(favoriteRuleKeys),
    [favoriteRuleKeys],
  );

  const favoritesCount = useMemo(
    () =>
      alertRules.filter((r) => favoriteKeySet.has(getAlertRuleFavoriteKey(r)))
        .length,
    [alertRules, favoriteKeySet],
  );

  const handleToggleAlertRuleFavorite = (rule, event) => {
    if (event) event.stopPropagation();
    const key = getAlertRuleFavoriteKey(rule);
    if (!key) return;
    setFavoriteRuleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return Array.from(next);
    });
  };

  const handleAddNewClick = async () => {
    // Fetch cooldown range from API
    try {
      const response = await userSettingsAPI.getCooldownRange();
      console.log("Cooldown range API response:", response);

      // Extract min and max from response
      // Response format might be: { min: 0, max: 3600 } or { data: { min: 0, max: 3600 } }
      const cooldownData = response?.data || response || {};
      const min = cooldownData.min !== undefined ? cooldownData.min : 0;
      const max = cooldownData.max !== undefined ? cooldownData.max : 3600;

      setCooldownMin(min);
      setCooldownMax(max);
      console.log("Cooldown range set:", { min, max });
    } catch (error) {
      console.error("Error fetching cooldown range:", error);
      // Use default values on error
      setCooldownMin(0);
      setCooldownMax(3600);
    }

    setEditingAlert(null);
    setEditingRuleApiId(null);
    setAlertFormViewOnly(false);
    setShowAddAlertForm(true);
  };

  // Convert schedule to display string (e.g. "Mon, Tue, Wed" or "Forever")
  const formatScheduleForDisplay = (schedule) => {
    if (!schedule || typeof schedule !== "object") return "Forever";
    const s = schedule.schedule || schedule;
    const dayNameMap = {
      sunday: "Sun",
      monday: "Mon",
      tuesday: "Tue",
      wednesday: "Wed",
      thursday: "Thu",
      friday: "Fri",
      saturday: "Sat",
    };
    const dayKeys = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const hasSlots = (day) => Array.isArray(s[day]) && s[day].length > 0;
    const daysWithSlots = dayKeys.filter((d) => hasSlots(d));
    if (daysWithSlots.length === 0 || daysWithSlots.length === 7) {
      return "Forever";
    }
    return daysWithSlots.map((d) => dayNameMap[d] || d).join(", ");
  };

  const handleAddNewAlert = async (data) => {
    try {
      const firstSource = data.selectedSources[0] || "";

      // Determine source_type from Define Rule Source (AI / IoT / System)
      let sourceType = "camera";
      if (data.sourceCategory === "IoT") {
        sourceType = "iot_device";
      } else if (data.sourceCategory === "System") {
        sourceType = "system";
      } else if (data.sourceCategory === "AI") {
        sourceType = "camera";
      } else if (firstSource.toLowerCase().includes("iot")) {
        sourceType = "iot_device";
      } else if (firstSource.toLowerCase().includes("sys")) {
        sourceType = "system";
      }

      const sourceIds = Array.isArray(data.source_ids)
        ? data.source_ids.map(String)
        : [];
      const roiIds = Array.isArray(data.roi_ids)
        ? data.roi_ids.map(String)
        : [];

      // Extract trigger IDs from actions (prioritize triggerId from API)
      const triggerIdsRaw = data.actions
        .map((action) => action.triggerId)
        .filter((id) => id !== null && id !== undefined);
      const triggerIds = triggerIdsRaw.map((id) => String(id));

      // Use schedule in new format (already converted in AddAlertRule component)
      // Format: { monday: [{ start: "09:00", end: "17:00" }], ... }
      let scheduleData = data.schedule || {};

      // Check if schedule has any time slots (all empty arrays = no selection)
      const hasTimeSlots = (s) =>
        s &&
        typeof s === "object" &&
        Object.values(s).some(
          (daySlots) => Array.isArray(daySlots) && daySlots.length > 0,
        );

      // If schedule is empty, invalid, or all days have empty arrays, use default (all days, all time)
      if (
        !scheduleData ||
        typeof scheduleData !== "object" ||
        Object.keys(scheduleData).length === 0 ||
        !hasTimeSlots(scheduleData)
      ) {
        // API format: { day: [{ start: "HH:mm", end: "HH:mm" }], ... }
        const allDaySlot = { start: "00:00", end: "23:59" };
        scheduleData = {
          monday: [allDaySlot],
          tuesday: [allDaySlot],
          wednesday: [allDaySlot],
          thursday: [allDaySlot],
          friday: [allDaySlot],
          saturday: [allDaySlot],
          sunday: [allDaySlot],
        };
      }

      scheduleData = normalizeScheduleTimesForRulesApi(scheduleData);

      // Map severity to API format
      const severityMap = {
        low: "low",
        medium: "medium",
        high: "high",
        critical: "critical",
        warning: "medium",
      };
      const apiSeverity = severityMap[data.severity?.toLowerCase()] || "medium";

      const alertTypeForApi = mapAlertTypeForRulesApi(data.alertType);
      const ruleData = {
        alert_name: data.alertName || "New Alert Rule",
        alert_type: alertTypeForApi,
        countdown_interval: parseInt(data.countdownInterval, 10) || 60,
        is_enabled: true,
        roi_ids: roiIds,
        rule_severity: apiSeverity,
        schedule: scheduleData,
        source_ids: sourceIds,
        source_type: sourceType,
        trigger_ids: triggerIds,
      };

      // Call API to create rule
      console.log(
        "Sending rule data to API:",
        JSON.stringify(ruleData, null, 2),
      );
      const response = await alertRulesAPI.create(ruleData);
      console.log("Alert rule created successfully:", response);

      // Get the rule ID from the response
      const createdRuleId =
        response.data?.id ??
        response.data?.data?.id ??
        response.id ??
        response.rule_id;

      if (createdRuleId) {
        try {
          // First: Fetch the created rule data from backend
          console.log("Fetching created rule data:", createdRuleId);
          const ruleDataResponse = await alertRulesAPI.get(createdRuleId);
          console.log("Rule data fetched:", ruleDataResponse);

          // Second: Fetch triggers for this rule using /triggers/rule/:rule_id
          console.log("Fetching triggers for rule:", createdRuleId);
          const triggersResponse = await triggersAPI.getByRuleId(createdRuleId);
          console.log("Triggers fetched for rule:", triggersResponse);
        } catch (fetchError) {
          console.error("Error fetching rule data or triggers:", fetchError);
          // Continue even if fetch fails - rule was created successfully
        }
      }

      setSnackbarMessage("Alert rule has been successfully created!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setShowAddAlertForm(false);
      if (!alertRulesOnly) {
        navigate("/administration/ai-models-mgmt?tab=alert-rules");
      }

      // Refresh rules list from API to show the newly created rule
      try {
        fetchingRulesRef.current = false;
        const rulesResponse = await alertRulesAPI.list();
        const rules =
          rulesResponse.data?.rules ||
          rulesResponse.rules ||
          (Array.isArray(rulesResponse.data) ? rulesResponse.data : null) ||
          (Array.isArray(rulesResponse) ? rulesResponse : []);
        if (Array.isArray(rules) && rules.length > 0) {
          loadAlertRulesFromAPI(rules);
        } else if (
          rules &&
          typeof rules === "object" &&
          !Array.isArray(rules)
        ) {
          loadAlertRulesFromAPI([rules]);
        } else {
          loadAlertRulesFromAPI([]);
        }
      } catch (refreshError) {
        console.error("Error refreshing rules list:", refreshError);
        // Don't show error to user as rule was created successfully
      }
    } catch (error) {
      console.error("Error creating alert:", error);
      console.error("Error details:", {
        status: error.status,
        message: error.message,
        errors: error.errors,
        data: error.data,
        response: error.response,
      });

      // Handle different error types with detailed messages
      let errorMessage = "Failed to create alert. Please try again.";
      if (error.status === 500) {
        errorMessage = `Server Error (500): ${
          error.data?.message ||
          error.message ||
          "Internal server error. Please check the console for details."
        }`;
      } else if (error.status === 422) {
        if (
          error.errors &&
          Array.isArray(error.errors) &&
          error.errors.length > 0
        ) {
          errorMessage = `Validation Error: ${error.errors
            .map((e) => e.message || e.field || e)
            .join(", ")}`;
        } else if (error.data) {
          if (error.data.errors) {
            errorMessage = `Validation Error: ${JSON.stringify(
              error.data.errors,
            )}`;
          } else if (error.data.message) {
            errorMessage = `Validation Error: ${error.data.message}`;
          } else {
            errorMessage = `Validation Error: ${JSON.stringify(error.data)}`;
          }
        } else if (error.message) {
          errorMessage = `Validation Error: ${error.message}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleUpdateAlert = async (data) => {
    if (editingRuleApiId || data.id) {
      try {
        const ruleApiId = editingRuleApiId || data.id;

        const firstSource = data.selectedSources[0] || "";

        let sourceType = "camera";
        if (data.sourceCategory === "IoT") {
          sourceType = "iot_device";
        } else if (data.sourceCategory === "System") {
          sourceType = "system";
        } else if (data.sourceCategory === "AI") {
          sourceType = "camera";
        } else if (firstSource.toLowerCase().includes("iot")) {
          sourceType = "iot_device";
        } else if (firstSource.toLowerCase().includes("sys")) {
          sourceType = "system";
        }

        const sourceIds = Array.isArray(data.source_ids)
          ? data.source_ids.map(String)
          : [];
        const roiIds = Array.isArray(data.roi_ids)
          ? data.roi_ids.map(String)
          : [];

        const triggerIdsRaw = data.actions
          .map((action) => action.triggerId)
          .filter((id) => id !== null && id !== undefined);
        const triggerIds = triggerIdsRaw.map((id) => String(id));

        // Use schedule in new format (already converted in AddAlertRule component)
        // Format: { monday: [{ start: "09:00", end: "17:00" }], ... }
        let scheduleData = data.schedule || {};

        // Check if schedule has any time slots (all empty arrays = no selection)
        const hasTimeSlots = (s) =>
          s &&
          typeof s === "object" &&
          Object.values(s).some(
            (daySlots) => Array.isArray(daySlots) && daySlots.length > 0,
          );

        // If schedule is empty, invalid, or all days have empty arrays, use default (all days, all time)
        if (
          !scheduleData ||
          typeof scheduleData !== "object" ||
          Object.keys(scheduleData).length === 0 ||
          !hasTimeSlots(scheduleData)
        ) {
          // API format: { day: [{ start: "HH:mm", end: "HH:mm" }], ... }
          const allDaySlot = { start: "00:00", end: "23:59" };
          scheduleData = {
            monday: [allDaySlot],
            tuesday: [allDaySlot],
            wednesday: [allDaySlot],
            thursday: [allDaySlot],
            friday: [allDaySlot],
            saturday: [allDaySlot],
            sunday: [allDaySlot],
          };
        }

        scheduleData = normalizeScheduleTimesForRulesApi(scheduleData);

        const alertTypeForApi = mapAlertTypeForRulesApi(data.alertType);
        const severityMap = {
          low: "low",
          medium: "medium",
          high: "high",
          critical: "critical",
          warning: "medium",
        };
        const apiSeverity =
          severityMap[data.severity?.toLowerCase()] || "medium";
        const updateData = {
          alert_name: data.alertName || data.name,
          alert_type: alertTypeForApi,
          countdown_interval: parseInt(data.countdownInterval || "0", 10) || 60,
          is_enabled: true,
          roi_ids: roiIds,
          rule_severity: apiSeverity,
          schedule: scheduleData,
          source_ids: sourceIds,
          source_type: sourceType,
          trigger_ids: triggerIds,
        };

        console.log("Updating rule:", ruleApiId, "with data:", updateData);
        const response = await alertRulesAPI.update(ruleApiId, updateData);
        console.log("Rule updated successfully:", response);

        // Also update local state for UI (include schedule for immediate display update)
        const actionTypes = data.actions.map((action) => action.type);
        const scheduleDisplay = formatScheduleForDisplay(data.schedule);
        updateAlertRule(data.id, {
          name: data.alertName || data.name,
          alertType:
            data.alertTypeDisplayName != null &&
            String(data.alertTypeDisplayName).trim() !== ""
              ? String(data.alertTypeDisplayName).trim()
              : data.alertType,
          actionTypes,
          schedule: scheduleDisplay,
        });

        setSnackbarMessage("Alert has been successfully updated!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setShowAddAlertForm(false);
        setEditingAlert(null);
        setEditingRuleApiId(null);
        if (!alertRulesOnly) {
          navigate("/administration/ai-models-mgmt?tab=alert-rules");
        }

        // Refresh rules list from API
        fetchingRulesRef.current = false;
        const rulesResponse = await alertRulesAPI.list();
        const rules =
          rulesResponse.data?.rules ||
          rulesResponse.rules ||
          (Array.isArray(rulesResponse.data) ? rulesResponse.data : null) ||
          (Array.isArray(rulesResponse) ? rulesResponse : []);
        if (Array.isArray(rules) && rules.length > 0) {
          loadAlertRulesFromAPI(rules);
        } else {
          loadAlertRulesFromAPI([]);
        }
      } catch (error) {
        console.error("Error updating rule:", error);
        let errorMessage = "Failed to update alert. Please try again.";
        if (error.status === 422) {
          errorMessage = `Validation Error: ${
            error.message || "Invalid data provided"
          }`;
        } else if (error.message) {
          errorMessage = error.message;
        }
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  // Date filter helper functions
  const getDateRangeDates = (range) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dates = { start: null, end: today };

    switch (range) {
      case "today":
        dates.start = new Date(today);
        break;
      case "yesterday":
        dates.start = new Date(today);
        dates.start.setDate(today.getDate() - 1);
        dates.end = new Date(today);
        dates.end.setDate(today.getDate() - 1);
        break;
      case "last7days":
        dates.start = new Date(today);
        dates.start.setDate(today.getDate() - 7);
        break;
      case "last30days":
        dates.start = new Date(today);
        dates.start.setDate(today.getDate() - 30);
        break;
      case "thisweek":
        const dayOfWeek = today.getDay();
        dates.start = new Date(today);
        dates.start.setDate(today.getDate() - dayOfWeek);
        break;
      case "thismonth":
        dates.start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case "custom":
        dates.start = startDate;
        dates.end = endDate;
        break;
      default:
        return null;
    }
    return dates;
  };

  const isDateInRange = (ruleDate, dateRange) => {
    if (!dateRange || dateRange === "all") return true;

    const dates = getDateRangeDates(dateRange);
    if (!dates || !dates.start) return true;

    const ruleDateObj = new Date(ruleDate);
    ruleDateObj.setHours(0, 0, 0, 0);

    const startDateObj = new Date(dates.start);
    startDateObj.setHours(0, 0, 0, 0);

    const endDateObj = dates.end ? new Date(dates.end) : new Date();
    endDateObj.setHours(23, 59, 59, 999);

    return ruleDateObj >= startDateObj && ruleDateObj <= endDateObj;
  };

  const getDateRangeDisplay = () => {
    const rangeMap = {
      all: "All dates",
      today: "Today",
      yesterday: "Yesterday",
      last7days: "Last 7 days",
      last30days: "Last 30 days",
      thisweek: "This week",
      thismonth: "This month",
      custom:
        startDate && endDate
          ? `${startDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })} - ${endDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}`
          : "Custom range",
    };
    return rangeMap[dateRange] || rangeMap.all;
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setDateRangeMenuAnchor(null);
    if (range === "custom") {
      setCustomDateDialog(true);
      if (startDate) {
        setViewingMonth(startDate);
      }
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleApplyCustomRange = () => {
    if (tempStartDate && tempEndDate) {
      setStartDate(tempStartDate);
      setEndDate(tempEndDate);
      setCustomDateDialog(false);
      setTempStartDate(null);
      setTempEndDate(null);
    }
  };

  const handleCancelCustomRange = () => {
    setCustomDateDialog(false);
    setTempStartDate(null);
    setTempEndDate(null);
    if (!startDate || !endDate) {
      setDateRange("all");
    }
  };

  const ruleAlertTypeFilterOptions = useMemo(() => {
    const byId = new Map();
    for (const uc of ruleTabUseCases) {
      byId.set(uc.id, { value: uc.id, label: uc.name || uc.id });
    }
    for (const id of selectedRuleAlertTypes) {
      if (id && !byId.has(id)) {
        byId.set(id, { value: id, label: id });
      }
    }
    return Array.from(byId.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
  }, [ruleTabUseCases, selectedRuleAlertTypes]);

  const ruleAlertTypeIdToLabel = useMemo(() => {
    const m = new Map();
    for (const o of ruleAlertTypeFilterOptions) m.set(o.value, o.label);
    return m;
  }, [ruleAlertTypeFilterOptions]);

  const ruleSourceFilterOptions = useMemo(() => {
    const byId = new Map();
    for (const c of ruleTabCameras) {
      byId.set(c.id, { value: c.id, label: c.name || c.id });
    }
    for (const id of selectedRuleSourceNames) {
      const sid = String(id ?? "").trim();
      if (sid && !byId.has(sid)) {
        byId.set(sid, { value: sid, label: sid });
      }
    }
    return Array.from(byId.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
  }, [ruleTabCameras, selectedRuleSourceNames]);

  const ruleSourceCameraIdToName = useMemo(() => {
    const m = new Map();
    for (const o of ruleSourceFilterOptions) m.set(o.value, o.label);
    return m;
  }, [ruleSourceFilterOptions]);

  const severityFilterOptions = useMemo(() => {
    const present = new Set();
    alertRules.forEach((r) => {
      const sl = r.severityLabel;
      if (sl == null || String(sl).trim() === "" || sl === "—") return;
      present.add(String(sl));
    });
    const ordered = SEVERITY_FILTER_ORDER.filter((s) => present.has(s));
    const rest = Array.from(present)
      .filter((s) => !SEVERITY_FILTER_ORDER.includes(s))
      .sort((a, b) => a.localeCompare(b));
    return [...ordered, ...rest];
  }, [alertRules]);

  const ruleStatusFilterOptions = useMemo(() => {
    let hasActive = false;
    let hasInactive = false;
    alertRules.forEach((r) => {
      if (r.isActive === true) hasActive = true;
      else if (r.isActive === false) hasInactive = true;
    });
    const opts = [];
    if (hasActive) opts.push({ value: "active", label: "Active" });
    if (hasInactive) opts.push({ value: "inactive", label: "Inactive" });
    return opts;
  }, [alertRules]);

  useEffect(() => {
    const allowed = new Set(ruleAlertTypeFilterOptions.map((o) => o.value));
    setSelectedRuleAlertTypes((prev) => prev.filter((id) => allowed.has(id)));
  }, [ruleAlertTypeFilterOptions]);

  useEffect(() => {
    const allowed = new Set(ruleSourceFilterOptions.map((o) => o.value));
    setSelectedRuleSourceNames((prev) => prev.filter((id) => allowed.has(id)));
  }, [ruleSourceFilterOptions]);

  useEffect(() => {
    const allowed = new Set(severityFilterOptions);
    setSelectedRuleSeverities((prev) => prev.filter((s) => allowed.has(s)));
  }, [severityFilterOptions]);

  useEffect(() => {
    const allowed = new Set(ruleStatusFilterOptions.map((o) => o.value));
    setSelectedRuleActiveStates((prev) => prev.filter((v) => allowed.has(v)));
  }, [ruleStatusFilterOptions]);

  const filteredAndSortedRules = useMemo(() => {
    let filtered = alertRules.filter((rule) => {
      if (!isRuleAlertTypeAllChecked && selectedRuleAlertTypes.length > 0) {
        if (
          !alertRuleMatchesUseCaseSelection(
            rule,
            selectedRuleAlertTypes,
            ruleAlertTypeIdToLabel,
          )
        ) {
          return false;
        }
      } else if (
        !isRuleAlertTypeAllChecked &&
        selectedRuleAlertTypes.length === 0
      ) {
        return false;
      }

      if (!isRuleSourceNameAllChecked && selectedRuleSourceNames.length > 0) {
        if (
          !alertRuleMatchesCameraSelection(
            rule,
            selectedRuleSourceNames,
            ruleSourceCameraIdToName,
          )
        ) {
          return false;
        }
      } else if (
        !isRuleSourceNameAllChecked &&
        selectedRuleSourceNames.length === 0
      ) {
        return false;
      }

      if (showFavoritesOnly) {
        const fk = getAlertRuleFavoriteKey(rule);
        if (!fk || !favoriteKeySet.has(fk)) return false;
      }

      if (!isRuleSeverityAllChecked && selectedRuleSeverities.length > 0) {
        if (
          !selectedRuleSeverities.includes(String(rule.severityLabel ?? ""))
        ) {
          return false;
        }
      } else if (
        !isRuleSeverityAllChecked &&
        selectedRuleSeverities.length === 0
      ) {
        return false;
      }

      if (!isRuleActiveStateAllChecked && selectedRuleActiveStates.length > 0) {
        const matchActive =
          selectedRuleActiveStates.includes("active") && rule.isActive === true;
        const matchInactive =
          selectedRuleActiveStates.includes("inactive") &&
          rule.isActive === false;
        if (!matchActive && !matchInactive) return false;
      } else if (
        !isRuleActiveStateAllChecked &&
        selectedRuleActiveStates.length === 0
      ) {
        return false;
      }

      const query = searchQuery.toLowerCase().trim();
      if (query) {
        const matchesSearch =
          String(rule.name || "")
            .toLowerCase()
            .includes(query) ||
          String(rule.alertType || "")
            .toLowerCase()
            .includes(query) ||
          String(rule.severityLabel || "")
            .toLowerCase()
            .includes(query) ||
          String(rule.sourceCategory || "")
            .toLowerCase()
            .includes(query) ||
          String(rule.sourceName || "")
            .toLowerCase()
            .includes(query) ||
          String(rule.notifiedTo || "")
            .toLowerCase()
            .includes(query) ||
          String(rule.actionTypesDisplay || "")
            .toLowerCase()
            .includes(query) ||
          String(rule.subSourceLabel || "")
            .toLowerCase()
            .includes(query) ||
          String(rule.sourceLabel || "")
            .toLowerCase()
            .includes(query) ||
          String(rule.srId || "")
            .toLowerCase()
            .includes(query);

        if (!matchesSearch) return false;
      }

      const ruleDate = rule.createdDate || rule.updatedDate || rule.date;
      if (ruleDate) {
        return isDateInRange(ruleDate, dateRange);
      }
      return true;
    });

    filtered.sort((a, b) => {
      if (orderBy === "isActive") {
        const ca = a.isActive === true ? 1 : 0;
        const cb = b.isActive === true ? 1 : 0;
        return order === "asc" ? ca - cb : cb - ca;
      }

      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === "actionTypesDisplay") {
        aValue =
          a.actionTypesDisplay ??
          (Array.isArray(a.actionTypes) ? a.actionTypes.join(", ") : "");
        bValue =
          b.actionTypesDisplay ??
          (Array.isArray(b.actionTypes) ? b.actionTypes.join(", ") : "");
      }

      if (orderBy === "subSourceLabel") {
        aValue = a.subSourceLabel ?? a.sourceName ?? "";
        bValue = b.subSourceLabel ?? b.sourceName ?? "";
      }

      if (orderBy === "sourceLabel") {
        aValue = a.sourceLabel ?? a.sourceCategory ?? "";
        bValue = b.sourceLabel ?? b.sourceCategory ?? "";
      }

      if (orderBy === "sourceCategory") {
        aValue = a.sourceCategory ?? a.sourceLabel ?? "";
        bValue = b.sourceCategory ?? b.sourceLabel ?? "";
      }

      if (orderBy === "sourceName") {
        aValue = a.sourceName ?? a.subSourceLabel ?? "";
        bValue = b.sourceName ?? b.subSourceLabel ?? "";
      }

      if (aValue === undefined || aValue === null) aValue = "";
      if (bValue === undefined || bValue === null) bValue = "";

      if (typeof aValue !== "string") {
        aValue = String(aValue);
      }
      if (typeof bValue !== "string") {
        bValue = String(bValue);
      }

      const comparison = aValue
        .toLowerCase()
        .localeCompare(bValue.toLowerCase());
      return order === "asc" ? comparison : -comparison;
    });
    return filtered;
  }, [
    alertRules,
    searchQuery,
    order,
    orderBy,
    dateRange,
    startDate,
    endDate,
    isRuleAlertTypeAllChecked,
    selectedRuleAlertTypes,
    ruleAlertTypeIdToLabel,
    isRuleSourceNameAllChecked,
    selectedRuleSourceNames,
    ruleSourceCameraIdToName,
    isRuleSeverityAllChecked,
    selectedRuleSeverities,
    isRuleActiveStateAllChecked,
    selectedRuleActiveStates,
    showFavoritesOnly,
    favoriteKeySet,
  ]);

  const paginatedRules = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredAndSortedRules.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedRules, page, rowsPerPage]);

  const getRuleAlertTypeToolbarDisplay = () => {
    if (isRuleAlertTypeAllChecked) return "Alert types";
    if (selectedRuleAlertTypes.length === 0) return "No alert types";
    if (selectedRuleAlertTypes.length === 1) {
      const id = selectedRuleAlertTypes[0];
      return ruleAlertTypeIdToLabel.get(id) ?? id;
    }
    return `${selectedRuleAlertTypes.length} selected`;
  };

  const getRuleSourceNameToolbarDisplay = () => {
    if (isRuleSourceNameAllChecked) return "Sub-sources";
    if (selectedRuleSourceNames.length === 0) return "No sub-sources";
    if (selectedRuleSourceNames.length === 1) {
      const id = selectedRuleSourceNames[0];
      return ruleSourceCameraIdToName.get(id) ?? id;
    }
    return `${selectedRuleSourceNames.length} selected`;
  };

  const ruleFilterMenuItemSx = {
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    py: 0.5,
  };

  if (showAddAlertForm) {
    return (
      <>
        <AddAlertRule
          onCancel={() => {
            setEditingAlert(null);
            setEditingRuleApiId(null);
            setAlertFormViewOnly(false);
            setPendingSubSourceBrowseRuleApiId(null);
            setShowAddAlertForm(false);
            setIsFooterVisible(true);
          }}
          onAdd={handleAddNewAlert}
          onUpdate={handleUpdateAlert}
          editingAlert={editingAlert}
          ruleApiId={editingRuleApiId}
          onRuleActivationUpdated={(nextEnabled) => {
            const id = editingAlert?.id;
            if (id != null) updateAlertRule(id, { isActive: nextEnabled });
            setEditingAlert((prev) => {
              if (!prev) return prev;
              const prevActions = Array.isArray(prev.actions)
                ? prev.actions
                : [];
              const actions = prevActions.map((a) => ({
                ...a,
                isActive: nextEnabled,
              }));
              return { ...prev, isActive: nextEnabled, actions };
            });
          }}
          viewOnly={alertFormViewOnly}
          pendingSubSourceBrowseRuleApiId={pendingSubSourceBrowseRuleApiId}
          onPendingSubSourceBrowseConsumed={() =>
            setPendingSubSourceBrowseRuleApiId(null)
          }
          onScheduleChange={(newSchedule) => {
            setEditingAlert((prev) =>
              prev ? { ...prev, schedule: newSchedule } : prev,
            );
          }}
          cooldownMin={cooldownMin}
          cooldownMax={cooldownMax}
        />
      </>
    );
  }

  const truncateSourceName = (sourceName, wordLimit = 3) => {
    const words = sourceName.split(" ");
    return words.length <= wordLimit
      ? sourceName
      : words.slice(0, wordLimit).join(" ") + "...";
  };

  /** Channel chip colors: SMS / Email / Telegram / default. */
  const notifyChannelChipSx = (type) => {
    const t = String(type || "").toLowerCase();
    if (t === "sms")
      return { backgroundColor: "#FFEBEE", color: "#C62828" };
    if (t === "email")
      return { backgroundColor: "#E8F5E9", color: "#2E7D32" };
    if (t === "telegram")
      return { backgroundColor: "#E0F7FA", color: "#00838F" };
    return { backgroundColor: "#E3F2FD", color: "#2932E5" };
  };

  const renderNotifiedByChips = (actionTypes) => {
    const list = Array.isArray(actionTypes) ? actionTypes : [];
    const visibleCount = 4;
    const visible = list.slice(0, visibleCount);
    const remaining = list.length - visibleCount;
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        flexWrap="wrap"
        gap={0.5}
      >
        {visible.map((type, index) => (
          <Chip
            key={`${type}-${index}`}
            label={type}
            size="small"
            sx={{
              fontSize: "0.75rem",
              height: "24px",
              fontWeight: 500,
              ...notifyChannelChipSx(type),
            }}
          />
        ))}
        {remaining > 0 && (
          <Tooltip title={list.slice(visibleCount).join(", ")}>
            <Chip
              label={`+${remaining}`}
              size="small"
              sx={{
                fontSize: "0.75rem",
                height: "24px",
                fontWeight: 500,
                backgroundColor: "#F5F5F5",
                color: "#616161",
              }}
            />
          </Tooltip>
        )}
      </Stack>
    );
  };

  const renderTableContent = () => {
    if (!alertRulesOnly && currentTab === 0) return <Dashboard />;
    if (!alertRulesOnly && currentTab === 1) return <HistoryEnhanced />;

    // If API hasn't responded yet, don't display anything for Alert Rules
    if (isRulesView && !hasApiResponse) {
      return null;
    }

    const emptyAlertRulesBody =
      showFavoritesOnly && favoritesCount === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 4,
            width: "100%",
            maxWidth: 480,
            mx: "auto",
            // bgcolor: "#FAFAFA",
            borderRadius: "8px",
            boxSizing: "border-box",
          }}
        >
          <StarBorderIcon sx={{ fontSize: 40, color: "#D1D5DB" }} />
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              color: "#4B5563",
              textAlign: "center",
            }}
          >
            No favorites yet
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#6B7280",
              textAlign: "center",
              maxWidth: 420,
              lineHeight: 1.5,
            }}
          >
            Click the star icon next to alert names to add them to favorites.
          </Typography>
        </Box>
      ) : alertRules.length === 0 ? (
        <EmptyState onAddNew={canCreateRule ? handleAddNewClick : undefined} />
      ) : (
        <EmptyState type="no-results" onAddNew={canCreateRule ? handleAddNewClick : undefined} />
      );

    return (
      <>
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "24px",
            boxShadow:
              "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
            display: "flex",
            flexDirection: "column",
            minHeight: "400px",
            marginLeft: "10px",
            marginRight: "10px",
          }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              backgroundColor: "#fafafa",
              px: 1,
              py: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              borderRadius: "20px 20px 0 0",
            }}
          >
            {/* Toolbar: search, views, type/source filters, severity/status */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                useFlexGap
                sx={{ flexWrap: "wrap", flex: 1, minWidth: 0 }}
              >
                <TextField
                  size="small"
                  placeholder="view alerts"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: "1 1 200px",
                    minWidth: 200,
                    maxWidth: 400,
                    width: { xs: "100%", sm: "auto" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "white",
                      height: "36px",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      "& fieldset": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#bdbdbd",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2932E5",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#9CA3AF",
                      opacity: 1,
                    },
                  }}
                />
                <Tooltip
                  title={
                    showFavoritesOnly
                      ? "Show all alert rules"
                      : "Show favorites only"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                    aria-label={
                      showFavoritesOnly
                        ? "Show all alert rules"
                        : "Show favorites only"
                    }
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "10px",
                      height: 36,
                      width: 36,
                      bgcolor: showFavoritesOnly ? "#FFE0D0" : "#fff",
                      "&:hover": {
                        bgcolor: showFavoritesOnly ? "#FFD0B8" : "#F5F5F5",
                      },
                    }}
                  >
                    <Badge
                      badgeContent={favoritesCount}
                      color="primary"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "10px",
                          height: "16px",
                          minWidth: "16px",
                          bgcolor: "#2932E5",
                          borderRadius: "10px",
                        },
                      }}
                    >
                      {showFavoritesOnly ? (
                        <StarIcon fontSize="small" sx={{ color: "#FF8555" }} />
                      ) : (
                        <StarBorderIcon
                          fontSize="small"
                          sx={{ color: "#9E9E9E" }}
                        />
                      )}
                    </Badge>
                  </IconButton>
                </Tooltip>
                <IconButton
                  onClick={() => setViewMode("list")}
                  size="small"
                  aria-label="List view"
                  sx={{
                    borderRadius: "8px",
                    color: viewMode === "list" ? "#2932E5" : "#6B7280",
                    backgroundColor:
                      viewMode === "list" ? "rgba(41, 50, 229, 0.08)" : "white",
                    border:
                      viewMode === "list"
                        ? "1px solid rgba(41, 50, 229, 0.35)"
                        : "1px solid #e5e7eb",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      backgroundColor:
                        viewMode === "list"
                          ? "rgba(41, 50, 229, 0.12)"
                          : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <ViewListIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => setViewMode("grid")}
                  size="small"
                  aria-label="Grid view"
                  sx={{
                    borderRadius: "8px",
                    color: viewMode === "grid" ? "#2932E5" : "#6B7280",
                    backgroundColor:
                      viewMode === "grid" ? "rgba(41, 50, 229, 0.08)" : "white",
                    border:
                      viewMode === "grid"
                        ? "1px solid rgba(41, 50, 229, 0.35)"
                        : "1px solid #e5e7eb",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      backgroundColor:
                        viewMode === "grid"
                          ? "rgba(41, 50, 229, 0.12)"
                          : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <ViewModuleIcon fontSize="small" />
                </IconButton>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => setRuleAlertTypeMenuAnchor(e.currentTarget)}
                  sx={{
                    minWidth: 148,
                    maxWidth: 220,
                    height: "36px",
                    borderRadius: "10px",
                    backgroundColor: "white",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    textTransform: "none",
                    color:
                      !isRuleAlertTypeAllChecked ||
                      selectedRuleAlertTypes.length > 0
                        ? "rgba(0, 0, 0, 0.87)"
                        : "rgba(0, 0, 0, 0.55)",
                    border: ruleAlertTypeMenuAnchor
                      ? "1px solid #2932E5"
                      : "1px solid #e0e0e0",
                    justifyContent: "space-between",
                    px: 1.25,
                    "&:hover": {
                      borderColor: "#bdbdbd",
                      backgroundColor: "white",
                    },
                  }}
                >
                  <Typography
                    component="span"
                    noWrap
                    sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
                  >
                    {getRuleAlertTypeToolbarDisplay()}
                  </Typography>
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => setRuleSourceNameMenuAnchor(e.currentTarget)}
                  aria-label="Sub-source"
                  sx={{
                    minWidth: 160,
                    maxWidth: 220,
                    height: "36px",
                    borderRadius: "10px",
                    backgroundColor: "white",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    textTransform: "none",
                    color:
                      !isRuleSourceNameAllChecked ||
                      selectedRuleSourceNames.length > 0
                        ? "rgba(0, 0, 0, 0.87)"
                        : "rgba(0, 0, 0, 0.55)",
                    border: ruleSourceNameMenuAnchor
                      ? "1px solid #2932E5"
                      : "1px solid #e0e0e0",
                    justifyContent: "space-between",
                    px: 1.25,
                    "&:hover": {
                      borderColor: "#bdbdbd",
                      backgroundColor: "white",
                    },
                  }}
                >
                  <Typography
                    component="span"
                    noWrap
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 14,
                      maxWidth: 160,
                    }}
                  >
                    {getRuleSourceNameToolbarDisplay()}
                  </Typography>
                </Button>
                {/* <Tooltip title={`Date: ${getDateRangeDisplay()}`}>
                  <IconButton
                    size="small"
                    onClick={(e) => setDateRangeMenuAnchor(e.currentTarget)}
                    aria-label="Filter by date range"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                      color: dateRangeMenuAnchor ? "#2932E5" : "#6B7280",
                      backgroundColor: "white",
                      border: dateRangeMenuAnchor
                        ? "1px solid #2932E5"
                        : "1px solid #e5e7eb",
                      "&:hover": {
                        backgroundColor: "rgba(41, 50, 229, 0.06)",
                        borderColor: "#2932E5",
                      },
                    }}
                  >
                    <CalendarTodayIcon fontSize="small" />
                  </IconButton>
                </Tooltip> */}
                <Tooltip title="Filter by severity and status">
                  <IconButton
                    size="small"
                    onClick={(e) =>
                      setSeverityStatusFilterAnchor(e.currentTarget)
                    }
                    aria-label="Filter by severity and status"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                      color: severityStatusFilterAnchor ? "#2932E5" : "#6B7280",
                      backgroundColor: "white",
                      border: severityStatusFilterAnchor
                        ? "1px solid #2932E5"
                        : !isRuleSeverityAllChecked ||
                            !isRuleActiveStateAllChecked
                          ? "1px solid #2932E5"
                          : "1px solid #e5e7eb",
                      "&:hover": {
                        backgroundColor: "rgba(41, 50, 229, 0.06)",
                        borderColor: "#2932E5",
                      },
                    }}
                  >
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                {canCreateRule ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddNewClick}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                    cursor: "pointer",
                    userSelect: "none",
                    letterSpacing: "0.02857em",
                    minWidth: "100px",
                    backgroundColor: "rgb(41, 50, 229)",
                    textTransform: "none",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    paddingTop: "5px",
                    paddingBottom: "5px",
                    height: "36px",
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "24.5px",
                    color: "white",
                    boxShadow: "none",
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor: "#1f28c7",
                      boxShadow: "none",
                    },
                  }}
                >
                  Add Rule
                </Button>
                ) : null}
                <Tooltip title="Settings">
                  <IconButton
                    size="small"
                    sx={{ color: "#666" }}
                    onClick={() => setShowSettingsDialog(true)}
                    aria-label="Alert rules settings"
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {/* Second Row: Sorting (only in grid view) */}
            {viewMode === "grid" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "rgba(0, 0, 0, 0.87)",
                  }}
                >
                  Sort by:
                </Typography>
                <Select
                  value={orderBy}
                  onChange={(e) => {
                    setOrderBy(e.target.value);
                    setPage(1); // Reset to first page when sorting changes
                  }}
                  size="small"
                  displayEmpty
                  sx={{
                    minWidth: "140px",
                    height: "36px",
                    borderRadius: "10px",
                    backgroundColor: "white",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2932E5",
                    },
                  }}
                >
                  <MenuItem
                    value="srId"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    id
                  </MenuItem>
                  <MenuItem
                    value="name"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Alert Name
                  </MenuItem>
                  <MenuItem
                    value="alertType"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Alert Type
                  </MenuItem>
                  <MenuItem
                    value="severityLabel"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Severity
                  </MenuItem>
                  <MenuItem
                    value="schedule"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Schedule
                  </MenuItem>
                  <MenuItem
                    value="sourceLabel"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Source
                  </MenuItem>
                  <MenuItem
                    value="subSourceLabel"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Sub Source
                  </MenuItem>
                  <MenuItem
                    value="notifiedTo"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Notified To
                  </MenuItem>
                  <MenuItem
                    value="actionTypesDisplay"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Notified By
                  </MenuItem>
                  <MenuItem
                    value="isActive"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Status
                  </MenuItem>
                </Select>
                <Select
                  value={order}
                  onChange={(e) => {
                    setOrder(e.target.value);
                    setPage(1); // Reset to first page when sorting changes
                  }}
                  size="small"
                  sx={{
                    minWidth: "120px",
                    height: "36px",
                    borderRadius: "10px",
                    backgroundColor: "white",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2932E5",
                    },
                  }}
                >
                  <MenuItem
                    value="asc"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Ascending
                  </MenuItem>
                  <MenuItem
                    value="desc"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Descending
                  </MenuItem>
                </Select>
              </Box>
            )}
          </Box>

          {/* Table */}
          {viewMode === "list" ? (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                height: "calc(100vh - 325px)",
                overflow: "auto",
              }}
            >
              <Table
                stickyHeader
                sx={{
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    borderBottom: "none",
                    padding: "0px 15px",
                    lineHeight: "18px",
                    height: "40px",
                    textAlign: "left",
                  },
                  "& .MuiTableBody-root .MuiTableCell-root": {
                    padding: "0px 15px",
                    lineHeight: "18px",
                    height: "40px",
                    textAlign: "left",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                        width: 100,
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "srId"}
                        direction={orderBy === "srId" ? order : "asc"}
                        onClick={() => handleRequestSort("srId")}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          color: "#2932E5 !important",
                          "&.Mui-active": {
                            color: "#2932E5 !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#2932E5 !important",
                          },
                        }}
                      >
                        ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "name"}
                        direction={orderBy === "name" ? order : "asc"}
                        onClick={() => handleRequestSort("name")}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          color: "#2932E5 !important",
                          "&.Mui-active": {
                            color: "#2932E5 !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#2932E5 !important",
                          },
                        }}
                      >
                        Alert Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "alertType"}
                        direction={orderBy === "alertType" ? order : "asc"}
                        onClick={() => handleRequestSort("alertType")}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          color: "#2932E5 !important",
                          "&.Mui-active": {
                            color: "#2932E5 !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#2932E5 !important",
                          },
                        }}
                      >
                        Alert Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "severityLabel"}
                        direction={
                          orderBy === "severityLabel" ? order : "asc"
                        }
                        onClick={() => handleRequestSort("severityLabel")}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          color: "#2932E5 !important",
                          "&.Mui-active": {
                            color: "#2932E5 !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#2932E5 !important",
                          },
                        }}
                      >
                        Severity
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "schedule"}
                        direction={orderBy === "schedule" ? order : "asc"}
                        onClick={() => handleRequestSort("schedule")}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          color: "#2932E5 !important",
                          "&.Mui-active": {
                            color: "#2932E5 !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#2932E5 !important",
                          },
                        }}
                      >
                        Schedule
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <TableSortLabel
                        active={
                          orderBy === "sourceLabel" ||
                          orderBy === "sourceCategory"
                        }
                        direction={
                          orderBy === "sourceLabel" ||
                          orderBy === "sourceCategory"
                            ? order
                            : "asc"
                        }
                        onClick={() => handleRequestSort("sourceLabel")}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          color: "#2932E5 !important",
                          "&.Mui-active": {
                            color: "#2932E5 !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#2932E5 !important",
                          },
                        }}
                      >
                        Source
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <TableSortLabel
                        active={
                          orderBy === "subSourceLabel" ||
                          orderBy === "sourceName"
                        }
                        direction={
                          orderBy === "subSourceLabel" ||
                          orderBy === "sourceName"
                            ? order
                            : "asc"
                        }
                        onClick={() => handleRequestSort("subSourceLabel")}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          color: "#2932E5 !important",
                          "&.Mui-active": {
                            color: "#2932E5 !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#2932E5 !important",
                          },
                        }}
                      >
                        Sub Source
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "notifiedTo"}
                        direction={orderBy === "notifiedTo" ? order : "asc"}
                        onClick={() => handleRequestSort("notifiedTo")}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          color: "#2932E5 !important",
                          "&.Mui-active": {
                            color: "#2932E5 !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#2932E5 !important",
                          },
                        }}
                      >
                        Notified To
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "actionTypesDisplay"}
                        direction={
                          orderBy === "actionTypesDisplay" ? order : "asc"
                        }
                        onClick={() => handleRequestSort("actionTypesDisplay")}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          color: "#2932E5 !important",
                          "&.Mui-active": {
                            color: "#2932E5 !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#2932E5 !important",
                          },
                        }}
                      >
                        Notified By
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "isActive"}
                        direction={orderBy === "isActive" ? order : "asc"}
                        onClick={() => handleRequestSort("isActive")}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          color: "#2932E5 !important",
                          "&.Mui-active": {
                            color: "#2932E5 !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#2932E5 !important",
                          },
                        }}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      width={48}
                      align="center"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#2932E5",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRules.length === 0 && hasApiResponse ? (
                    <TableRow>
                      <TableCell
                        colSpan={11}
                        align="center"
                        sx={{
                          p: 0,
                          verticalAlign: "middle",
                          borderBottom: "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: {
                              xs: "min(56vh, 400px)",
                              sm: "min(52vh, 440px)",
                            },
                            width: "100%",
                            py: { xs: 3, sm: 4 },
                            px: 2,
                            boxSizing: "border-box",
                          }}
                        >
                          {emptyAlertRulesBody}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : paginatedRules.length === 0 && !hasApiResponse ? null : ( // Don't display anything if API hasn't responded
                    paginatedRules.map((rule) => {
                      return (
                      <TableRow
                        key={rule.id}
                        hover
                        aria-selected={hoveredRow === rule.id}
                        onMouseEnter={() => setHoveredRow(rule.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        onContextMenu={(e) => handleContextMenu(e, rule)}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.875rem",
                            color: "rgba(0,0,0,0.87)",
                          }}
                        >
                          {rule.srId ? (
                            <Typography
                              component="span"
                              sx={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "0.875rem",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {rule.srId}
                            </Typography>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#212121",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {rule.name}
                            <IconButton
                              size="small"
                              aria-label={
                                favoriteKeySet.has(
                                  getAlertRuleFavoriteKey(rule),
                                )
                                  ? "Remove from favorites"
                                  : "Add to favorites"
                              }
                              onClick={(e) =>
                                handleToggleAlertRuleFavorite(rule, e)
                              }
                              sx={{
                                padding: "4px",
                                opacity: hoveredRow === rule.id ? 1 : 0,
                                transition: "opacity 0.2s",
                              }}
                            >
                              {favoriteKeySet.has(
                                getAlertRuleFavoriteKey(rule),
                              ) ? (
                                <StarIcon
                                  sx={{ fontSize: 16, color: "#FF8555" }}
                                />
                              ) : (
                                <StarBorderIcon
                                  sx={{ fontSize: 16, color: "#9E9E9E" }}
                                />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.875rem",
                          }}
                        >
                          {rule.alertType}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.875rem",
                          }}
                        >
                          {rule.severityLabel ?? "Medium"}
                        </TableCell>
                        <TableCell sx={{}}>
                          <Chip
                            label={rule.schedule || "Forever"}
                            size="small"
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              height: "24px",
                              backgroundColor: "#FFF3E0",
                              color: "#E65100",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.875rem",
                          }}
                        >
                          {rule.sourceLabel ?? rule.sourceCategory ?? "—"}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              minWidth: 0,
                            }}
                          >
                            <VideocamIcon
                              sx={{
                                fontSize: 18,
                                color: "#2932E5",
                                flexShrink: 0,
                              }}
                            />
                            <Tooltip
                              title={
                                rule.subSourceLabel ||
                                rule.sourceName ||
                                ""
                              }
                            >
                              <Button
                                type="button"
                                variant="text"
                                disabled={!ruleCanInteractSubSourceColumn(rule)}
                                onClick={(e) =>
                                  handleSubSourceColumnClick(e, rule)
                                }
                                sx={{
                                  fontFamily: "Roboto, sans-serif",
                                  fontSize: "0.875rem",
                                  fontWeight: 500,
                                  textTransform: "none",
                                  p: 0,
                                  minWidth: 0,
                                  justifyContent: "flex-start",
                                  color: "rgba(0, 0, 0, 0.87)",
                                  textDecoration: "none",
                                  "&:hover:not(.Mui-disabled)": {
                                    color: "#2932E5",
                                    textDecoration: "underline",
                                    textDecorationColor: "rgba(41, 50, 229, 0.35)",
                                    bgcolor: "rgba(41, 50, 229, 0.04)",
                                  },
                                  "&.Mui-disabled": {
                                    color: "rgba(0, 0, 0, 0.38)",
                                    textDecoration: "none",
                                  },
                                }}
                              >
                                <Typography
                                  component="span"
                                  noWrap
                                  sx={{
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                  }}
                                >
                                  {rule.subSourceLabel ||
                                    rule.sourceName ||
                                    "—"}
                                </Typography>
                              </Button>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Tooltip
                            title={rule.notifiedTo || "—"}
                            disableHoverListener={
                              !(rule.notifiedTo || "").trim()
                            }
                          >
                            <Typography
                              noWrap
                              sx={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "0.875rem",
                              }}
                            >
                              {truncateSourceName(
                                rule.notifiedTo || "—",
                                8,
                              )}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          {renderNotifiedByChips(rule.actionTypes || [])}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={rule.isActive ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              height: "24px",
                              backgroundColor: rule.isActive
                                ? "#E8F5E9"
                                : "#FFEBEE",
                              color: rule.isActive ? "#2E7D32" : "#C62828",
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ width: 48 }}>
                          <IconButton
                            size="small"
                            aria-label="Row actions"
                            onClick={(e) => handleMenuOpen(e, rule)}
                            sx={{
                              color: "#4A5565",
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : paginatedRules.length === 0 ? (
            <Box
              sx={{
                minHeight: { xs: "min(56vh, 400px)", sm: "min(52vh, 440px)" },
                overflow: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                py: 3,
                // bgcolor: "#FAFAFA",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 720,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {emptyAlertRulesBody}
              </Box>
            </Box>
          ) : (
            <AlertRuleGridView
              rules={paginatedRules}
              onMenuOpen={handleMenuOpen}
              onContextMenu={handleContextMenu}
              favoriteKeys={favoriteRuleKeys}
              onToggleFavorite={handleToggleAlertRuleFavorite}
              onSubSourceClick={handleSubSourceColumnClick}
            />
          )}

          {/* Table Footer */}
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#f2f2f2",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "0.875rem",
                color: "#666",
              }}
            >
              Total Items: {filteredAndSortedRules.length}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "0.875rem",
                  color: "#666",
                }}
              >
                Rows per page:
              </Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                variant="standard"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "0.875rem",
                  "&:before": {
                    borderBottom: "none",
                  },
                  "&:after": {
                    borderBottom: "none",
                  },
                  "&:hover:not(.Mui-disabled):before": {
                    borderBottom: "none",
                  },
                }}
              >
                {[10, 25, 50].map((v) => (
                  <MenuItem
                    key={v}
                    value={v}
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    {v}
                  </MenuItem>
                ))}
              </Select>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "0.875rem",
                  color: "#666",
                }}
              >
                {Math.min(
                  (page - 1) * rowsPerPage + 1,
                  filteredAndSortedRules.length,
                )}
                -{Math.min(page * rowsPerPage, filteredAndSortedRules.length)}{" "}
                of {filteredAndSortedRules.length}
              </Typography>
              <IconButton
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                size="small"
                aria-label="Previous page"
                sx={{
                  "&:disabled": {
                    opacity: 0.5,
                  },
                }}
              >
                <KeyboardArrowLeftIcon fontSize="small" />
              </IconButton>
              <IconButton
                disabled={
                  page >= Math.ceil(filteredAndSortedRules.length / rowsPerPage)
                }
                onClick={() =>
                  setPage((p) =>
                    Math.min(
                      Math.ceil(filteredAndSortedRules.length / rowsPerPage),
                      p + 1,
                    ),
                  )
                }
                size="small"
                aria-label="Next page"
                sx={{
                  "&:disabled": {
                    opacity: 0.5,
                  },
                }}
              >
                <KeyboardArrowRightIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <>
      <Box sx={{ backgroundColor: "#f2f2f2" }}>
        {!alertRulesOnly && (
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "17.5px",
              color: "#4A5565",
              minHeight: "48px",
              padding: "12px 1px",
              flexDirection: "row",
              borderBottom: "none",
              "& .MuiTabs-indicator": {
                backgroundColor: "#2932E5",
                height: "2px",
              },
              "& .MuiTab-root": {
                fontFamily: "Roboto, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "17.5px",
                color: "#4A5565",
                minHeight: "48px",
                padding: "12px 16px",
                textTransform: "none",
                "&.Mui-selected": {
                  color: "#2932E5",
                },
                "& .MuiTab-iconWrapper": {
                  color: "inherit",
                  marginRight: "8px",
                },
              },
            }}
          >
            <Tab
              label="Dashboard"
              icon={<DashboardIcon />}
              iconPosition="start"
              sx={{
                "&.Mui-selected .MuiTab-iconWrapper": {
                  color: "#2932E5",
                },
              }}
            />
            <Tab
              label="Alert History"
              icon={<HistoryIcon />}
              iconPosition="start"
              sx={{
                "&.Mui-selected .MuiTab-iconWrapper": {
                  color: "#2932E5",
                },
              }}
            />
            {/* <Tab
              label="Alert Rules"
              icon={<NotificationsIcon />}
              iconPosition="start"
              sx={{
                "&.Mui-selected .MuiTab-iconWrapper": {
                  color: "#2932E5",
                },
              }}
            /> */}
          </Tabs>
        )}
        {renderTableContent()}
      </Box>

      {/* Menus and Dialogs */}
      {/* Date Range Menu */}
      {/* <Menu
        anchorEl={dateRangeMenuAnchor}
        open={Boolean(dateRangeMenuAnchor)}
        onClose={() => setDateRangeMenuAnchor(null)}
        PaperProps={{
          sx: { minWidth: 180 },
        }}
      >
        {[
          { value: "all", label: "All dates" },
          { value: "today", label: "Today" },
          { value: "yesterday", label: "Yesterday" },
          { value: "last7days", label: "Last 7 days" },
          { value: "last30days", label: "Last 30 days" },
          { value: "thisweek", label: "This week" },
          { value: "thismonth", label: "This month" },
          { value: "custom", label: "Custom range" },
        ].map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleDateRangeChange(option.value)}
            selected={dateRange === option.value}
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              "&.Mui-selected": {
                backgroundColor: "rgba(41, 50, 229, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(41, 50, 229, 0.12)",
                },
              },
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu> */}

      {/* Severity & status filter (multi-select + All clear / All check) */}
      <Menu
        anchorEl={severityStatusFilterAnchor}
        open={Boolean(severityStatusFilterAnchor)}
        onClose={() => setSeverityStatusFilterAnchor(null)}
        disableAutoFocus
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxHeight: "min(380px, 70vh)",
            overflow: "auto",
            p: 1,
            pt: 0.5,
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 600,
              color: "rgba(0, 0, 0, 0.87)",
            }}
          >
            Severity
          </Typography>
        </Box>
        {severityFilterOptions.length === 0 ? (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              No severity values in current rules
            </Typography>
          </Box>
        ) : (
          <>
            <MenuItem
              onClick={() => {
                setPage(1);
                if (isRuleSeverityAllChecked) {
                  setIsRuleSeverityAllChecked(false);
                  setSelectedRuleSeverities([]);
                  return;
                }
                setIsRuleSeverityAllChecked(true);
                setSelectedRuleSeverities([]);
              }}
              sx={{
                ...ruleFilterMenuItemSx,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Checkbox
                checked={isRuleSeverityAllChecked}
                size="small"
                tabIndex={-1}
                disableRipple
                sx={{ p: 0 }}
              />
              <Typography
                component="span"
                sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
              >
                {isRuleSeverityAllChecked ? "All clear" : "All check"}
              </Typography>
            </MenuItem>
            {severityFilterOptions.map((s) => (
              <MenuItem
                key={s}
                onClick={() => {
                  setPage(1);
                  if (isRuleSeverityAllChecked) {
                    setIsRuleSeverityAllChecked(false);
                    setSelectedRuleSeverities(
                      severityFilterOptions.filter((item) => item !== s),
                    );
                    return;
                  }
                  setSelectedRuleSeverities((prev) => {
                    const next = prev.includes(s)
                      ? prev.filter((x) => x !== s)
                      : [...prev, s];
                    const allSelected = severityFilterOptions.every((item) =>
                      next.includes(item),
                    );
                    if (allSelected) {
                      setIsRuleSeverityAllChecked(true);
                      return [];
                    }
                    return next;
                  });
                }}
                sx={{
                  ...ruleFilterMenuItemSx,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Checkbox
                  checked={
                    isRuleSeverityAllChecked ||
                    selectedRuleSeverities.includes(s)
                  }
                  size="small"
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: 0 }}
                />
                <Typography
                  component="span"
                  sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
                >
                  {s}
                </Typography>
              </MenuItem>
            ))}
          </>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ px: 1.5, py: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 600,
              color: "rgba(0, 0, 0, 0.87)",
            }}
          >
            Status
          </Typography>
        </Box>
        {ruleStatusFilterOptions.length === 0 ? (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              No status values in current rules
            </Typography>
          </Box>
        ) : (
          <>
            <MenuItem
              onClick={() => {
                setPage(1);
                if (isRuleActiveStateAllChecked) {
                  setIsRuleActiveStateAllChecked(false);
                  setSelectedRuleActiveStates([]);
                  return;
                }
                setIsRuleActiveStateAllChecked(true);
                setSelectedRuleActiveStates([]);
              }}
              sx={{
                ...ruleFilterMenuItemSx,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Checkbox
                checked={isRuleActiveStateAllChecked}
                size="small"
                tabIndex={-1}
                disableRipple
                sx={{ p: 0 }}
              />
              <Typography
                component="span"
                sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
              >
                {isRuleActiveStateAllChecked ? "All clear" : "All check"}
              </Typography>
            </MenuItem>
            {ruleStatusFilterOptions.map((o) => (
              <MenuItem
                key={o.value}
                onClick={() => {
                  setPage(1);
                  const values = ruleStatusFilterOptions.map((x) => x.value);
                  if (isRuleActiveStateAllChecked) {
                    setIsRuleActiveStateAllChecked(false);
                    setSelectedRuleActiveStates(
                      values.filter((v) => v !== o.value),
                    );
                    return;
                  }
                  setSelectedRuleActiveStates((prev) => {
                    const next = prev.includes(o.value)
                      ? prev.filter((x) => x !== o.value)
                      : [...prev, o.value];
                    const allSelected = values.every((v) => next.includes(v));
                    if (allSelected) {
                      setIsRuleActiveStateAllChecked(true);
                      return [];
                    }
                    return next;
                  });
                }}
                sx={{
                  ...ruleFilterMenuItemSx,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Checkbox
                  checked={
                    isRuleActiveStateAllChecked ||
                    selectedRuleActiveStates.includes(o.value)
                  }
                  size="small"
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: 0 }}
                />
                <Typography
                  component="span"
                  sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
                >
                  {o.label}
                </Typography>
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      <Menu
        anchorEl={ruleAlertTypeMenuAnchor}
        open={Boolean(ruleAlertTypeMenuAnchor)}
        onClose={() => setRuleAlertTypeMenuAnchor(null)}
        disableAutoFocus
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            mt: 0.5,
            minWidth: ruleAlertTypeMenuAnchor
              ? ruleAlertTypeMenuAnchor.clientWidth
              : 180,
            maxHeight: 320,
          },
        }}
      >
        {ruleTabUseCasesLoading ? (
          <Box
            sx={{
              px: 2,
              py: 2,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : ruleAlertTypeFilterOptions.length === 0 ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              No use cases available
            </Typography>
          </Box>
        ) : (
          <>
            <MenuItem
              onClick={() => {
                setPage(1);
                if (isRuleAlertTypeAllChecked) {
                  setIsRuleAlertTypeAllChecked(false);
                  setSelectedRuleAlertTypes([]);
                  return;
                }
                setIsRuleAlertTypeAllChecked(true);
                setSelectedRuleAlertTypes([]);
              }}
              sx={{
                ...ruleFilterMenuItemSx,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Checkbox
                checked={isRuleAlertTypeAllChecked}
                size="small"
                tabIndex={-1}
                disableRipple
                sx={{ p: 0 }}
              />
              <Typography
                component="span"
                sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
              >
                {isRuleAlertTypeAllChecked ? "All clear" : "All check"}
              </Typography>
            </MenuItem>
            {ruleAlertTypeFilterOptions.map((opt) => (
              <MenuItem
                key={opt.value}
                onClick={() => {
                  setPage(1);
                  const allValues = ruleAlertTypeFilterOptions.map((o) => o.value);
                  if (isRuleAlertTypeAllChecked) {
                    setIsRuleAlertTypeAllChecked(false);
                    setSelectedRuleAlertTypes(
                      allValues.filter((id) => id !== opt.value),
                    );
                    return;
                  }
                  setSelectedRuleAlertTypes((prev) => {
                    const next = prev.includes(opt.value)
                      ? prev.filter((x) => x !== opt.value)
                      : [...prev, opt.value];
                    const allSelected = allValues.every((id) =>
                      next.includes(id),
                    );
                    if (allSelected) {
                      setIsRuleAlertTypeAllChecked(true);
                      return [];
                    }
                    return next;
                  });
                }}
                sx={{
                  ...ruleFilterMenuItemSx,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Checkbox
                  checked={
                    isRuleAlertTypeAllChecked ||
                    selectedRuleAlertTypes.includes(opt.value)
                  }
                  size="small"
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: 0 }}
                />
                <Typography
                  component="span"
                  sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
                >
                  {opt.label}
                </Typography>
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      <Menu
        anchorEl={ruleSourceNameMenuAnchor}
        open={Boolean(ruleSourceNameMenuAnchor)}
        onClose={() => setRuleSourceNameMenuAnchor(null)}
        disableAutoFocus
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            mt: 0.5,
            minWidth: ruleSourceNameMenuAnchor
              ? ruleSourceNameMenuAnchor.clientWidth
              : 180,
            maxHeight: 320,
          },
        }}
      >
        {ruleTabCamerasLoading ? (
          <Box
            sx={{
              px: 2,
              py: 2,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : ruleSourceFilterOptions.length === 0 ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              No cameras available
            </Typography>
          </Box>
        ) : (
          <>
            <MenuItem
              onClick={() => {
                setPage(1);
                if (isRuleSourceNameAllChecked) {
                  setIsRuleSourceNameAllChecked(false);
                  setSelectedRuleSourceNames([]);
                  return;
                }
                setIsRuleSourceNameAllChecked(true);
                setSelectedRuleSourceNames([]);
              }}
              sx={{
                ...ruleFilterMenuItemSx,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Checkbox
                checked={isRuleSourceNameAllChecked}
                size="small"
                tabIndex={-1}
                disableRipple
                sx={{ p: 0 }}
              />
              <Typography
                component="span"
                sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
              >
                {isRuleSourceNameAllChecked ? "All clear" : "All check"}
              </Typography>
            </MenuItem>
            {ruleSourceFilterOptions.map((opt) => (
              <MenuItem
                key={opt.value}
                onClick={() => {
                  setPage(1);
                  const allValues = ruleSourceFilterOptions.map((o) => o.value);
                  if (isRuleSourceNameAllChecked) {
                    setIsRuleSourceNameAllChecked(false);
                    setSelectedRuleSourceNames(
                      allValues.filter((id) => id !== opt.value),
                    );
                    return;
                  }
                  setSelectedRuleSourceNames((prev) => {
                    const next = prev.includes(opt.value)
                      ? prev.filter((x) => x !== opt.value)
                      : [...prev, opt.value];
                    const allSelected = allValues.every((id) =>
                      next.includes(id),
                    );
                    if (allSelected) {
                      setIsRuleSourceNameAllChecked(true);
                      return [];
                    }
                    return next;
                  });
                }}
                sx={{
                  ...ruleFilterMenuItemSx,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Checkbox
                  checked={
                    isRuleSourceNameAllChecked ||
                    selectedRuleSourceNames.includes(opt.value)
                  }
                  size="small"
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: 0 }}
                />
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  noWrap
                >
                  {opt.label}
                </Typography>
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      {/* Custom Date Range Picker Dialog */}
      <Dialog
        open={customDateDialog}
        onClose={handleCancelCustomRange}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: "white",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            p: 2,
            pb: 1,
            backgroundColor: "white",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Typography
            sx={{
              lineHeight: "1.6",
              letterSpacing: "0.0075em",
              flex: "0 0 auto",
              fontFamily: "Roboto, sans-serif",
              fontSize: "20px",
              color: "rgb(255, 133, 85)",
              fontWeight: 600,
            }}
          >
            Custom Date Range
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 2, backgroundColor: "#F2F2F2" }}>
          {/* Date Input Fields */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, mt: "10px" }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                placeholder="mm/dd/yyyy"
                value={
                  tempStartDate
                    ? tempStartDate.toLocaleDateString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      })
                    : ""
                }
                onChange={(e) => {
                  const dateStr = e.target.value;
                  const dateMatch = dateStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
                  if (dateMatch) {
                    const [, month, day, year] = dateMatch;
                    const date = new Date(year, month - 1, day);
                    if (!isNaN(date.getTime())) {
                      setTempStartDate(date);
                      setViewingMonth(date);
                    }
                  } else if (dateStr === "") {
                    setTempStartDate(null);
                  }
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarTodayIcon sx={{ color: "#4A5565" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "Roboto, sans-serif",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bdbdbd",
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2932E5",
                  },
                  "& .MuiInputBase-input": {
                    font: "inherit",
                    letterSpacing: "inherit",
                    color: "currentcolor",
                    border: "0px",
                    boxSizing: "content-box",
                    background: "none",
                    height: "1.4375em",
                    margin: "0px",
                    WebkitTapHighlightColor: "transparent",
                    display: "block",
                    minWidth: "0px",
                    width: "100%",
                    animationName: "mui-auto-fill-cancel",
                    animationDuration: "10ms",
                    padding: "16.5px 14px",
                  },
                }}
              />
              <Typography
                sx={{
                  color: "rgba(0, 0, 0, 0.6)",
                  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 400,
                  fontSize: "0.75rem",
                  lineHeight: 1.66,
                  letterSpacing: "0.03333em",
                  textAlign: "left",
                  margin: 0,
                  ml: 1.5,
                }}
              >
                Start Date
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                placeholder="mm/dd/yyyy"
                value={
                  tempEndDate
                    ? tempEndDate.toLocaleDateString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      })
                    : ""
                }
                onChange={(e) => {
                  const dateStr = e.target.value;
                  const dateMatch = dateStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
                  if (dateMatch) {
                    const [, month, day, year] = dateMatch;
                    const date = new Date(year, month - 1, day);
                    if (!isNaN(date.getTime())) {
                      setTempEndDate(date);
                    }
                  } else if (dateStr === "") {
                    setTempEndDate(null);
                  }
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarTodayIcon sx={{ color: "#4A5565" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "Roboto, sans-serif",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bdbdbd",
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2932E5",
                  },
                  "& .MuiInputBase-input": {
                    font: "inherit",
                    letterSpacing: "inherit",
                    color: "currentcolor",
                    border: "0px",
                    boxSizing: "content-box",
                    background: "none",
                    height: "1.4375em",
                    margin: "0px",
                    WebkitTapHighlightColor: "transparent",
                    display: "block",
                    minWidth: "0px",
                    width: "100%",
                    animationName: "mui-auto-fill-cancel",
                    animationDuration: "10ms",
                    padding: "16.5px 14px",
                  },
                }}
              />
              <Typography
                sx={{
                  color: "rgba(0, 0, 0, 0.6)",
                  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 400,
                  fontSize: "0.75rem",
                  lineHeight: 1.66,
                  letterSpacing: "0.03333em",
                  textAlign: "left",
                  margin: 0,
                  ml: 1.5,
                }}
              >
                End Date
              </Typography>
            </Box>
          </Box>

          {/* Calendar View */}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "12px",
              p: 2,
            }}
          >
            {/* Month Navigation */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                pb: 1.5,
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <IconButton
                onClick={() => {
                  const prevMonth = new Date(viewingMonth);
                  prevMonth.setMonth(prevMonth.getMonth() - 1);
                  setViewingMonth(prevMonth);
                }}
                sx={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  width: "36px",
                  height: "36px",
                  "&:hover": {
                    backgroundColor: "rgba(41, 50, 229, 0.08)",
                    borderColor: "#2932E5",
                  },
                }}
              >
                <KeyboardArrowLeftIcon sx={{ color: "#4A5565" }} />
              </IconButton>

              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#2932E5",
                }}
              >
                {viewingMonth.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </Typography>

              <IconButton
                onClick={() => {
                  const nextMonth = new Date(viewingMonth);
                  nextMonth.setMonth(nextMonth.getMonth() + 1);
                  setViewingMonth(nextMonth);
                }}
                sx={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  width: "36px",
                  height: "36px",
                  "&:hover": {
                    backgroundColor: "rgba(41, 50, 229, 0.08)",
                    borderColor: "#2932E5",
                  },
                }}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    color: "#4A5565",
                    transform: "rotate(-90deg)",
                  }}
                />
              </IconButton>
            </Box>

            {/* Calendar Grid */}
            <Box>
              {/* Day Headers */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <Typography
                      key={day}
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#4A5565",
                        textAlign: "center",
                        py: 1,
                      }}
                    >
                      {day}
                    </Typography>
                  ),
                )}
              </Box>

              {/* Calendar Days */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 0.5,
                }}
              >
                {(() => {
                  const year = viewingMonth.getFullYear();
                  const month = viewingMonth.getMonth();
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  const daysInMonth = lastDay.getDate();
                  const startDayOfWeek = firstDay.getDay();

                  const days = [];
                  // Empty cells for days before month starts
                  for (let i = 0; i < startDayOfWeek; i++) {
                    days.push(
                      <Box
                        key={`empty-${i}`}
                        sx={{
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />,
                    );
                  }

                  // Days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(year, month, day);
                    const isToday =
                      date.toDateString() === new Date().toDateString();
                    const isStartDate =
                      tempStartDate &&
                      date.toDateString() === tempStartDate.toDateString();
                    const isEndDate =
                      tempEndDate &&
                      date.toDateString() === tempEndDate.toDateString();
                    const isSelected = isStartDate || isEndDate;
                    const isInRange =
                      tempStartDate &&
                      tempEndDate &&
                      date >= tempStartDate &&
                      date <= tempEndDate;

                    days.push(
                      <Box
                        key={day}
                        onClick={() => {
                          if (
                            !tempStartDate ||
                            (tempStartDate && tempEndDate)
                          ) {
                            // Reset selection or start new selection
                            setTempStartDate(date);
                            setTempEndDate(null);
                          } else if (date < tempStartDate) {
                            // Start date is after clicked date, swap them
                            setTempEndDate(tempStartDate);
                            setTempStartDate(date);
                          } else {
                            // Set end date
                            setTempEndDate(date);
                          }
                        }}
                        sx={{
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected
                            ? "#FFFFFF"
                            : isToday
                              ? "#2932E5"
                              : "#212121",
                          backgroundColor: isSelected
                            ? "#2932E5"
                            : isInRange
                              ? "rgba(41, 50, 229, 0.1)"
                              : isToday
                                ? "rgba(41, 50, 229, 0.08)"
                                : "transparent",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? "#2932E5"
                              : "rgba(41, 50, 229, 0.1)",
                          },
                        }}
                      >
                        {day}
                      </Box>,
                    );
                  }

                  return days;
                })()}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            pt: 2,
            backgroundColor: "white",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => {
                setTempStartDate(null);
                setTempEndDate(null);
              }}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "#4A5565",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Clear
            </Button>
            <Button
              onClick={() => {
                const today = new Date();
                setTempStartDate(today);
                setTempEndDate(today);
                setViewingMonth(today);
              }}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "#4A5565",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Today
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={handleCancelCustomRange}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "#4A5565",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyCustomRange}
              variant="contained"
              disabled={!tempStartDate || !tempEndDate}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                backgroundColor: "#2932E5",
                borderRadius: "8px",
                px: 3,
                "&:hover": {
                  backgroundColor: "#1f28c7",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              Apply
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Menu
        open={Boolean(anchorEl) || Boolean(contextMenuPosition)}
        onClose={handleMenuClose}
        anchorReference={
          contextMenuPosition ? "anchorPosition" : "anchorEl"
        }
        anchorEl={contextMenuPosition ? null : anchorEl}
        anchorPosition={
          contextMenuPosition
            ? {
                top: contextMenuPosition.top,
                left: contextMenuPosition.left,
              }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            void handleView();
          }}
        >
          <VisibilityIcon sx={{ mr: 1 }} /> View
        </MenuItem>
        {canUpdateRule ? (
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        ) : null}
        {canUpdateRule ? (
        <MenuItem
          onClick={() => {
            if (selectedAlert) handleToggleActive(selectedAlert);
            handleMenuClose();
          }}
        >
          <CheckCircleIcon sx={{ mr: 1 }} />
          {selectedAlert?.isActive ? "Deactivate" : "Activate"}
        </MenuItem>
        ) : null}
        {canDeleteRule ? (
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
        ) : null}
      </Menu>

      <Dialog
        open={Boolean(subSourceRefsDialog)}
        onClose={() => setSubSourceRefsDialog(null)}
        PaperProps={{
          sx: {
            maxWidth: 700,
            width: "calc(100% - 32px)",
            borderRadius: 2,
            border: "1px solid #c5cae9",
            boxShadow:
              "0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(41, 50, 229, 0.06)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontWeight: 600,
            fontSize: "1.125rem",
            py: 2,
            px: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            borderBottom: "1px solid #e8eaef",
            mb: 2,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 600,
              fontSize: "1.125rem",
              color: "#212121",
              minWidth: 0,
              pr: 1,
              lineHeight: 1.35,
            }}
          >
            {(subSourceRefsDialog?.ruleName || "Alert rule").trim() ||
              "Alert rule"}{" "}
            - Source Name and Zone
          </Typography>
          <IconButton
            aria-label="Close"
            size="small"
            onClick={() => setSubSourceRefsDialog(null)}
            sx={{ color: "#5f6368" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            fontFamily: "Roboto, sans-serif",
            pt: 3.5,
            px: 2.5,
            pb: 2,
          }}
        >
          <Box
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              pr: 0.5,
              mr: -0.5,
            }}
          >
            {(subSourceRefsDialog?.accordionItems || []).map((row, index) => {
              const panelKey = subSourceAccordionItemKey(row, index);
              const zoneNames = (row.zoneRefsList || [])
                .map((z) => z.name || z.id)
                .filter(Boolean);
              const zonesLine =
                zoneNames.length > 0 ? zoneNames.join(", ") : "—";
              return (
                <Accordion
                  key={`${panelKey}-${index}`}
                  disableGutters
                  elevation={0}
                  expanded={subSourceAccordionExpanded === panelKey}
                  onChange={(_, isExpanded) => {
                    if (isExpanded) {
                      setSubSourceAccordionExpanded(panelKey);
                    } else if (subSourceAccordionExpanded === panelKey) {
                      setSubSourceAccordionExpanded(null);
                    }
                  }}
                  sx={{
                    border: "1px solid #e8eaef",
                    borderRadius: "8px !important",
                    mb: 1,
                    "&:before": { display: "none" },
                    boxShadow: "none",
                    overflow: "hidden",
                    "&:last-of-type": { mb: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#5f6368" }} />}
                    sx={{
                      minHeight: 48,
                      px: 1.5,
                      "& .MuiAccordionSummary-content": {
                        my: 1,
                        alignItems: "center",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.9375rem",
                        color: "#212121",
                      }}
                    >
                      {row.name || row.id || "—"}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 400,
                        fontSize: "0.875rem",
                        color: "#5f6368",
                        lineHeight: 1.5,
                      }}
                    >
                      {zonesLine}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "rgb(255, 255, 255)",
            color: "rgba(0, 0, 0, 0.87)",
            boxShadow:
              "rgba(0, 0, 0, 0.2) 0px 11px 15px -7px, rgba(0, 0, 0, 0.14) 0px 24px 38px 3px, rgba(0, 0, 0, 0.12) 0px 9px 46px 8px",
            position: "relative",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100% - 64px)",
            maxWidth: "600px",
            width: "calc(100% - 64px)",
            transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)",
            borderRadius: "4px",
            margin: "32px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Roboto, sans-serif",
            margin: "0px",
            color: "#D32F2F",
            fontWeight: 500,
            fontSize: "1.25rem",
            lineHeight: "1.6",
            letterSpacing: "0.0075em",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent
          sx={{
            fontFamily: "Roboto, sans-serif",
            margin: "0px 0px 16px",
            fontWeight: 400,
            fontSize: "1rem",
            lineHeight: "1.5",
            letterSpacing: "0.00938em",
            color: "rgba(0, 0, 0, 0.6)",
          }}
        >
          Are you sure you want to delete rule "
          {selectedAlert?.name || selectedAlert?.alertName || "this rule"}"?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ fontFamily: "Roboto, sans-serif" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            sx={{ fontFamily: "Roboto, sans-serif" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
      </Snackbar>

      {/* Settings Dialog */}
      <AlertRulesSettings
        open={showSettingsDialog}
        onClose={() => setShowSettingsDialog(false)}
      />
    </>
  );
}

export default function MainAlertServices({ alertRulesOnly = false }) {
  if (alertRulesOnly) {
    return <AppContent alertRulesOnly />;
  }
  return (
    <AlertDataProvider>
      <AppContent alertRulesOnly={false} />
    </AlertDataProvider>
  );
}
